// In-memory stand-in for the backend, used ONLY by the public demo build.
//
// Most of the CRM runs off the SEED_* dataset in deskte-full.jsx, which lives
// in React state and therefore works with no server at all. A handful of
// modules are server-owned instead — they fetch on mount and render an empty
// state when the fetch fails — so in the demo they showed up blank:
// quotes, workflows, email sequences, recurring invoices and calendar events.
//
// Rather than special-casing each of those components, this module answers the
// handful of endpoints they use. `apiFetch` (AuthGate.jsx) consults it first
// when IS_DEMO; anything not listed here still rejects, exactly as before, so
// the demo can never reach the real API.
//
// The stores are plain module-level arrays: a visitor can create, edit, pause
// and delete for real, and a page reload puts everything back. Nothing is
// persisted and nothing leaves the browser.
//
// The clients referenced here (c1, c2, c3, c5, c6, c9 …) are the invented ones
// from SEED_CLIENTS — keep them in sync if that roster ever changes.

import { plantillaProfesional } from './panelDePolizas.js';
import { PRODUCTOS_UHC, PLANES_UHC, UHC_CARRIER } from './components/catalogoPlanesUhc.js';
import { PLANTILLAS_DE_ENCUESTA, contenidoDePlantilla, enIdioma } from './encuestasPlantillas.js';
import { readLang } from './demoChrome.js';
import { HOSPITALES_DEMO } from './demoHospitales.js';
import { normalizarCondado } from './hospitalesAyuda.js';

// ── El cotizador del Mercado ─────────────────────────────────────────────────
//
// En el CRM esto llama a la API de HealthCare.gov a traves del servidor. En la
// demo no hay servidor, asi que el cotizador —que es de las cosas que un
// visitante quiere probar— se quedaba en "backend disabled" y no enseñaba ni un
// plan. Se contestan sus tres rutas con planes INVENTADOS de Florida.
//
// Los nombres de aseguradora son reales porque son publicos y operan en el
// estado; los precios, deducibles y copagos NO lo son y estan redondeados a
// proposito para que nadie los tome por una cotizacion.
const PLANES_DEL_MERCADO = [
  {
    id: 'demo-plan-1', name: 'Silver 344 Standard', issuer: 'AETNA CVS Health',
    metalLevel: 'Silver', planType: 'HMO',
    premium: 512, netPremium: 84, premiumWithCredit: 84,
    deductible: 1500, deductibleHealth: 1000, deductibleDrugs: 500, moop: 8700,
    hsaEligible: false, qualityRating: 4,
    copayPrimary: '$25', copaySpecialist: '$60', copayDrugGeneric: '$10',
    copayUrgent: '$75', copayER: '$500 despues del deducible',
    copayMentalHealth: '$25', diagnosticTest: '$40', imaging: '50% coaseguro',
    benefitsUrl: null, brochureUrl: null,
  },
  {
    id: 'demo-plan-2', name: 'Bronce 205 HSA', issuer: 'Florida Blue',
    metalLevel: 'Bronze', planType: 'PPO',
    premium: 398, netPremium: 0, premiumWithCredit: 0,
    deductible: 7500, deductibleHealth: null, deductibleDrugs: null, moop: 9200,
    hsaEligible: true, qualityRating: 3,
    copayPrimary: '40% despues del deducible', copaySpecialist: '40% despues del deducible',
    copayDrugGeneric: '$20', copayUrgent: '40% despues del deducible',
    copayER: '40% despues del deducible', copayMentalHealth: 'Sin cargo',
    diagnosticTest: '40% despues del deducible', imaging: '40% despues del deducible',
    benefitsUrl: null, brochureUrl: null,
  },
  {
    id: 'demo-plan-3', name: 'Oro 118 Plus', issuer: 'Ambetter',
    metalLevel: 'Gold', planType: 'HMO',
    premium: 690, netPremium: 262, premiumWithCredit: 262,
    deductible: 0, deductibleHealth: null, deductibleDrugs: null, moop: 6500,
    hsaEligible: false, qualityRating: 4,
    copayPrimary: 'Sin cargo', copaySpecialist: '$40', copayDrugGeneric: '$5',
    copayUrgent: '$50', copayER: '$350', copayMentalHealth: 'Sin cargo',
    diagnosticTest: 'Sin cargo', imaging: '$300', benefitsUrl: null, brochureUrl: null,
  },
  {
    id: 'demo-plan-4', name: 'Silver 512 Select', issuer: 'Molina Healthcare',
    metalLevel: 'Silver', planType: 'EPO',
    premium: 486, netPremium: 58, premiumWithCredit: 58,
    deductible: 2500, deductibleHealth: 2000, deductibleDrugs: 500, moop: 8100,
    hsaEligible: false, qualityRating: 3,
    copayPrimary: '$30', copaySpecialist: '$75', copayDrugGeneric: '$15',
    copayUrgent: '$90', copayER: '$600', copayMentalHealth: '$30',
    diagnosticTest: '$50', imaging: '45% coaseguro', benefitsUrl: null, brochureUrl: null,
  },
  {
    // Uno SIN copagos a proposito: en el Mercado los hay, y la pantalla tiene
    // que enseñar su raya en vez de un hueco (regla 158).
    id: 'demo-plan-5', name: 'Catastrofico 001', issuer: 'Oscar Health',
    metalLevel: 'Catastrophic', planType: 'EPO',
    premium: 305, netPremium: 305, premiumWithCredit: null,
    deductible: 9200, deductibleHealth: null, deductibleDrugs: null, moop: 9200,
    hsaEligible: false, qualityRating: null,
    copayPrimary: null, copaySpecialist: null, copayDrugGeneric: null,
    copayUrgent: null, copayER: null, copayMentalHealth: null,
    diagnosticTest: null, imaging: null, benefitsUrl: null, brochureUrl: null,
  },
];

// ── date helpers ─────────────────────────────────────────────────────────────
// Everything is relative to "today" so the demo never looks abandoned: a
// prospect opening it months from now still sees a quote sent last week and a
// renewal invoice due next month.
const DAY = 86400000;
const at = (days, hour = 10, min = 0) => {
  const d = new Date(Date.now() + days * DAY);
  d.setHours(hour, min, 0, 0);
  return d.toISOString();
};
const dateOnly = (days) => at(days).slice(0, 10);

let idSeq = 0;
const newId = (prefix) => `${prefix}_demo_${++idSeq}_${(idSeq * 7919).toString(36)}`;

// ── stores ───────────────────────────────────────────────────────────────────

const quotes = [
  {
    id: 'q1', number: 'Q-2601', title: 'Medicare Advantage 2026 — Maria Rodriguez',
    clientId: 'c1', recipientName: 'Maria Rodriguez', recipientEmail: 'm.rodriguez@example.com',
    status: 'ACCEPTED', viewedAt: at(-9, 18, 42), createdAt: at(-12), validUntil: dateOnly(18),
    notes: 'Prefiere mantener su cardiologo. Plan Aetna cubre la red completa.',
    convertedPolicyId: null, lead: null,
    items: [
      { id: 'qi1', description: 'Aetna Medicare Eagle (PPO)', carrier: 'Aetna', planType: 'MEDICARE', unitPrice: 0, effectiveDate: dateOnly(24), coverageDetails: { deductible: '$0', maxOutOfPocket: '$5,900', primaryCare: '$0 copay', specialist: '$35 copay', dental: 'Incluido' } },
      { id: 'qi2', description: 'Humana Gold Plus (HMO)', carrier: 'Humana', planType: 'MEDICARE', unitPrice: 0, effectiveDate: dateOnly(24), coverageDetails: { deductible: '$0', maxOutOfPocket: '$4,200', primaryCare: '$0 copay', specialist: '$45 copay', dental: 'Limitado' } },
      { id: 'qi3', description: 'UnitedHealthcare AARP (PPO)', carrier: 'UnitedHealthcare', planType: 'MEDICARE', unitPrice: 29, effectiveDate: dateOnly(24), coverageDetails: { deductible: '$0', maxOutOfPocket: '$6,700', primaryCare: '$10 copay', specialist: '$40 copay', dental: 'Incluido' } },
    ],
  },
  {
    id: 'q2', number: 'Q-2602', title: 'ACA Silver — familia Villanueva',
    clientId: 'c9', recipientName: 'Carmen Villanueva', recipientEmail: 'cvillanueva@example.com',
    status: 'SENT', viewedAt: at(-2, 21, 5), createdAt: at(-5), validUntil: dateOnly(25),
    notes: 'Hogar de 5 personas, ingreso $141,000. Comparar con y sin dental.',
    convertedPolicyId: null, lead: null,
    items: [
      { id: 'qi4', description: 'Oscar Silver Select PPO', carrier: 'Oscar', planType: 'HEALTH', unitPrice: 185, effectiveDate: dateOnly(31), coverageDetails: { deductible: '$3,500', maxOutOfPocket: '$8,700', primaryCare: '$25 copay', generic: '$10' } },
      { id: 'qi5', description: 'CareFirst BlueChoice Silver', carrier: 'CareFirst', planType: 'HEALTH', unitPrice: 212, effectiveDate: dateOnly(31), coverageDetails: { deductible: '$2,900', maxOutOfPocket: '$8,200', primaryCare: '$20 copay', generic: '$5' } },
    ],
  },
  {
    id: 'q3', number: 'Q-2603', title: 'Term Life 20 anos — Carlos Mendoza',
    clientId: 'c6', recipientName: 'Carlos Mendoza', recipientEmail: 'cmendoza@example.com',
    status: 'DRAFT', viewedAt: null, createdAt: at(-1), validUntil: dateOnly(29),
    notes: '', convertedPolicyId: null, lead: null,
    items: [
      { id: 'qi6', description: 'Term Life $500,000 · 20 anos', carrier: 'Mutual of Omaha', planType: 'LIFE', unitPrice: 47.8, effectiveDate: dateOnly(15), coverageDetails: { faceAmount: '$500,000', term: '20 anos', riders: 'Waiver of premium' } },
      { id: 'qi7', description: 'Term Life $750,000 · 20 anos', carrier: 'Banner Life', planType: 'LIFE', unitPrice: 66.4, effectiveDate: dateOnly(15), coverageDetails: { faceAmount: '$750,000', term: '20 anos', riders: 'Waiver of premium + Child rider' } },
    ],
  },
  {
    id: 'q4', number: 'Q-2604', title: 'Auto + Hogar (paquete) — Patricia Williams',
    clientId: 'c7', recipientName: 'Patricia Williams', recipientEmail: 'pwilliams@example.com',
    status: 'CONVERTED', viewedAt: at(-31, 12, 3), createdAt: at(-38), validUntil: dateOnly(-8),
    notes: 'Descuento por paquete aplicado.', convertedPolicyId: 'p7', lead: null,
    items: [
      { id: 'qi8', description: 'Auto + Homeowners bundle', carrier: 'Progressive', planType: 'AUTO', unitPrice: 268.5, effectiveDate: dateOnly(-30), coverageDetails: { liability: '100/300/100', deductible: '$500', bundleDiscount: '12%' } },
    ],
  },
  {
    id: 'q5', number: 'Q-2605', title: 'Commercial GL — Ramirez Construction',
    clientId: 'c8', recipientName: 'Diego Ramirez', recipientEmail: 'dramirez@example.com',
    status: 'DECLINED', viewedAt: at(-20, 9, 30), createdAt: at(-24), validUntil: dateOnly(-3),
    notes: 'Se fue con el corredor actual por $40/mes menos. Recontactar en renovacion.',
    convertedPolicyId: null, lead: null,
    items: [
      { id: 'qi9', description: 'General Liability $1M/$2M', carrier: 'The Hartford', planType: 'COMMERCIAL', unitPrice: 412, effectiveDate: dateOnly(-15), coverageDetails: { occurrence: '$1,000,000', aggregate: '$2,000,000', employees: '12' } },
    ],
  },
];

// InvoicesView derives the status from sentAt / viewedAt / payments vs total,
// so the fixtures set those rather than a status string.
const invoices = [
  {
    id: 'inv1', number: 'INV-2601', clientId: 'c9', recipientName: 'Carmen Villanueva', recipientEmail: 'cvillanueva@example.com',
    issueDate: dateOnly(-22), dueDate: dateOnly(-8), currency: 'USD', title: 'Prima ACA — mes en curso',
    items: [{ id: 'ii1', description: 'ACA Silver Select PPO — prima mensual', qty: 1, rate: 185 }],
    discount: 0, discountType: 'amount', taxRate: 0, terms: 'Net 14', notes: '',
    payments: [{ id: 'pay1', date: dateOnly(-19), amount: 185, method: 'card', reference: 'ch_demo_4411' }],
    sentAt: at(-22, 9), viewedAt: at(-21, 20, 14), createdAt: at(-22),
  },
  {
    id: 'inv2', number: 'INV-2602', clientId: 'c2', recipientName: 'James Thompson', recipientEmail: 'jthompson@example.com',
    issueDate: dateOnly(-6), dueDate: dateOnly(8), currency: 'USD', title: 'Medigap Plan G — prima mensual',
    items: [{ id: 'ii2', description: 'Medigap Plan G — prima mensual', qty: 1, rate: 187 }],
    discount: 0, discountType: 'amount', taxRate: 0, terms: 'Net 14', notes: '',
    payments: [],
    sentAt: at(-6, 9), viewedAt: at(-5, 11, 2), createdAt: at(-6),
  },
  {
    id: 'inv3', number: 'INV-2603', clientId: 'c3', recipientName: 'Ana Gutierrez', recipientEmail: 'anag@example.com',
    issueDate: dateOnly(-40), dueDate: dateOnly(-12), currency: 'USD', title: 'ACA Silver — prima mensual',
    items: [{ id: 'ii3', description: 'CareFirst ACA Silver — prima mensual', qty: 1, rate: 215 }],
    discount: 0, discountType: 'amount', taxRate: 0, terms: 'Net 14',
    notes: 'Segundo recordatorio enviado. Llamar si no paga esta semana.',
    payments: [],
    sentAt: at(-40, 9), viewedAt: at(-38, 19, 40), createdAt: at(-40),
  },
  {
    id: 'inv4', number: 'INV-2604', clientId: 'c8', recipientName: 'Diego Ramirez', recipientEmail: 'dramirez@example.com',
    issueDate: dateOnly(-14), dueDate: dateOnly(14), currency: 'USD', title: 'Poliza comercial — primer pago',
    items: [
      { id: 'ii4', description: 'General Liability $1M/$2M — cuota inicial', qty: 1, rate: 412 },
      { id: 'ii5', description: 'Certificado de seguro (emision)', qty: 2, rate: 15 },
    ],
    discount: 0, discountType: 'amount', taxRate: 0, terms: 'Net 30', notes: '',
    payments: [{ id: 'pay2', date: dateOnly(-9), amount: 200, method: 'transfer', reference: 'ACH-8821' }],
    sentAt: at(-14, 10), viewedAt: at(-13, 8, 55), createdAt: at(-14),
  },
  {
    id: 'inv5', number: 'INV-2605', clientId: 'c6', recipientName: 'Carlos Mendoza', recipientEmail: 'cmendoza@example.com',
    issueDate: dateOnly(0), dueDate: dateOnly(14), currency: 'USD', title: 'Term Life — primera prima',
    items: [{ id: 'ii6', description: 'Term Life $500,000 · 20 anos — primera prima', qty: 1, rate: 47.8 }],
    discount: 0, discountType: 'amount', taxRate: 0, terms: 'Net 14', notes: '',
    payments: [], sentAt: null, viewedAt: null, createdAt: at(0),
  },
];

// ── Conversaciones ──────────────────────────────────────────────────────────
//
// La bandeja unificada es de las que se alimentan SOLO del servidor: junta
// `SmsMessage` y `ClientEmailMessage`, que no viven en el SEED_* de
// deskte-full.jsx. Sin esto la demo enseñaria el modulo vacio, y es de los
// que mas se enseñan en una demostracion.
//
// Los clientes son los inventados de siempre (c1, c3, c6, c9) y los textos
// estan escritos a mano: nunca puede haber aqui datos de una persona real.
const conversaciones = [
  {
    id: 'cv1', channel: 'sms', clientId: 'c1', leadId: null,
    clientName: 'Maria Rodriguez',
    lastMessageAt: at(0, 9, 12), lastDirection: 'inbound',
    preview: 'Buenos dias! Me llego la carta de la aseguradora, la puede revisar?',
    unreadCount: 2, status: 'open', snoozedUntil: null,
    assigneeId: null, assigneeName: null,
  },
  {
    id: 'cv2', channel: 'email', clientId: 'c9', leadId: null,
    clientName: 'Carmen Villanueva',
    lastMessageAt: at(0, 8, 40), lastDirection: 'outbound',
    preview: 'Le adjunto el resumen de su plan para el ano que viene.',
    unreadCount: 0, status: 'open', snoozedUntil: null,
    assigneeId: 'u2', assigneeName: 'Agent',
  },
  {
    id: 'cv3', channel: 'sms', clientId: 'c6', leadId: null,
    clientName: 'Carlos Mendoza',
    lastMessageAt: at(-1, 17, 5), lastDirection: 'inbound',
    preview: 'Gracias! Quedamos asi entonces.',
    unreadCount: 0, status: 'open', snoozedUntil: null,
    assigneeId: null, assigneeName: null,
  },
  {
    id: 'cv4', channel: 'email', clientId: 'c3', leadId: null,
    clientName: 'Ana Gutierrez',
    lastMessageAt: at(-3, 11, 30), lastDirection: 'inbound',
    preview: 'Ya mande los documentos de ingreso que me pidio.',
    unreadCount: 1, status: 'open', snoozedUntil: null,
    assigneeId: null, assigneeName: null,
  },
];

const mensajesDeLaDemo = {
  cv1: [
    { id: 'm11', direction: 'outbound', subject: null, body: 'Hola Maria, le recuerdo que su renovacion abre el 1 de noviembre.', createdAt: at(-2, 10, 0) },
    { id: 'm12', direction: 'inbound', subject: null, body: 'Perfecto, gracias por avisar.', createdAt: at(-2, 10, 14) },
    { id: 'm13', direction: 'inbound', subject: null, body: 'Buenos dias! Me llego la carta de la aseguradora, la puede revisar?', createdAt: at(0, 9, 12) },
  ],
  cv2: [
    { id: 'm21', direction: 'inbound', subject: 'Consulta sobre mi plan', body: 'Buenas, queria saber si mi plan cambia el ano que viene.', createdAt: at(-1, 15, 20) },
    { id: 'm22', direction: 'outbound', subject: 'Re: Consulta sobre mi plan', body: 'Le adjunto el resumen de su plan para el ano que viene.', createdAt: at(0, 8, 40) },
  ],
  cv3: [
    { id: 'm31', direction: 'outbound', subject: null, body: 'Carlos, le agende la cita para el jueves a las 3.', createdAt: at(-1, 16, 50) },
    { id: 'm32', direction: 'inbound', subject: null, body: 'Gracias! Quedamos asi entonces.', createdAt: at(-1, 17, 5) },
  ],
  cv4: [
    { id: 'm41', direction: 'inbound', subject: 'Documentos', body: 'Ya mande los documentos de ingreso que me pidio.', createdAt: at(-3, 11, 30) },
  ],
};

// ── El embudo arrastrable ───────────────────────────────────────────────────
//
// El tablero se lo arma el SERVIDOR (columnas, totales y las primeras tarjetas
// de cada una), asi que sin esto la demo enseñaria un embudo vacio — y es de
// las pantallas que mas venden. Las tarjetas son leads inventados; los mueve
// de verdad el visitante y se reinicia al recargar.
const columnasDelEmbudo = [
  { id: 'et1', name: 'Nuevo',      position: 0, color: '#00d4ff', mappedStatus: 'NEW' },
  { id: 'et2', name: 'Contactado', position: 1, color: '#a78bfa', mappedStatus: 'CONTACTED' },
  { id: 'et3', name: 'Calificado', position: 2, color: '#06d6a0', mappedStatus: 'QUALIFIED' },
  { id: 'et4', name: 'Propuesta',  position: 3, color: '#ffb700', mappedStatus: 'PROPOSAL' },
  { id: 'et5', name: 'Ganado',     position: 4, color: '#22c55e', mappedStatus: 'WON' },
];

const tarjetasDelEmbudo = [
  { id: 'ld1', stageId: 'et1', firstName: 'Marisol', lastName: 'Vega',   phone: '305-555-2201', email: null, status: 'NEW',       source: 'Formulario web', language: 'spanish', followUpDate: dateOnly(0),  value: 1200, expectedCloseAt: dateOnly(14) },
  { id: 'ld2', stageId: 'et1', firstName: 'Tomas',   lastName: 'Rivera', phone: '786-555-2202', email: null, status: 'NEW',       source: 'Referido',       language: 'spanish', followUpDate: dateOnly(2),  value: null, expectedCloseAt: null },
  { id: 'ld3', stageId: 'et2', firstName: 'Claudia', lastName: 'Nieto',  phone: '407-555-2203', email: null, status: 'CONTACTED', source: 'Facebook',       language: 'spanish', followUpDate: dateOnly(-1), value: 2400, expectedCloseAt: dateOnly(21) },
  { id: 'ld4', stageId: 'et3', firstName: 'Héctor',  lastName: 'Palma',  phone: '813-555-2204', email: null, status: 'QUALIFIED', source: 'Llamada',        language: 'spanish', followUpDate: dateOnly(5),  value: 3600, expectedCloseAt: dateOnly(10) },
  { id: 'ld5', stageId: 'et4', firstName: 'Denise',  lastName: 'Aguilar',phone: '954-555-2205', email: null, status: 'PROPOSAL',  source: 'Referido',       language: 'english', followUpDate: dateOnly(1),  value: 5200, expectedCloseAt: dateOnly(7) },
  { id: 'ld6', stageId: 'et5', firstName: 'Rafael',  lastName: 'Sosa',   phone: '305-555-2206', email: null, status: 'WON',       source: 'Formulario web', language: 'spanish', followUpDate: null,         value: 4100, expectedCloseAt: dateOnly(-3) },
];

/** El tablero tal y como lo arma el servidor de verdad. */
function tableroDeLaDemo() {
  return {
    pipeline: { id: 'emb1', name: 'Embudo de ventas', isDefault: true },
    pipelines: [{ id: 'emb1', name: 'Embudo de ventas', isDefault: true }],
    perColumn: 50,
    stages: columnasDelEmbudo.map((col) => {
      const dentro = tarjetasDelEmbudo.filter((l) => l.stageId === col.id);
      return {
        ...col,
        total: dentro.length,
        valueSum: dentro.reduce((s, l) => s + (l.value || 0), 0),
        hasMore: false,
        leads: dentro.map((l) => ({ ...l, agent: { id: 'u2', displayName: 'Agent' } })),
      };
    }),
  };
}

const workflows = [
  {
    id: 'w1', name: 'Bienvenida a nuevo cliente', triggerType: 'client.created', isActive: true,
    runCount: 34, lastRunAt: at(-1, 9, 12), lastError: null,
    actions: [
      { type: 'send_email', params: { subject: 'Bienvenido a la agencia — tus proximos pasos' } },
      { type: 'create_task', params: { title: 'Llamada de bienvenida', dueInDays: 2 } },
      { type: 'wait', params: { days: 7, hours: 0 } },
      { type: 'send_email', params: { subject: 'Como usar tu portal de documentos' } },
    ],
  },
  {
    id: 'w2', name: 'Renovacion 30 dias antes', triggerType: 'policy.expiring', isActive: true,
    runCount: 118, lastRunAt: at(0, 7, 0), lastError: null,
    actions: [
      { type: 'create_task', params: { title: 'Revisar renovacion y cotizar alternativa', dueInDays: 3 } },
      { type: 'send_email', params: { subject: 'Tu poliza renueva pronto — revisemos juntos' } },
      { type: 'send_sms', params: { body: 'Hola! Tu poliza renueva en 30 dias. Te llamo esta semana para revisarla.' } },
    ],
  },
  {
    id: 'w3', name: 'Lead nuevo del formulario', triggerType: 'lead.created', isActive: true,
    runCount: 61, lastRunAt: at(-2, 16, 44), lastError: null,
    actions: [
      { type: 'send_email', params: { subject: 'Gracias por escribirnos — agenda tu cita' } },
      { type: 'create_task', params: { title: 'Llamar al lead nuevo', dueInDays: 1 } },
      { type: 'enroll_in_sequence', params: { sequenceId: 's2' } },
    ],
  },
  {
    id: 'w4', name: 'Cumpleanos del cliente', triggerType: 'client.birthday', isActive: true,
    runCount: 27, lastRunAt: at(-4, 8, 0), lastError: null,
    actions: [{ type: 'send_email', params: { subject: 'Feliz cumpleanos!' } }],
  },
  {
    id: 'w5', name: 'Documento recibido — avisar al equipo', triggerType: 'document.uploaded', isActive: false,
    runCount: 9, lastRunAt: at(-46, 11, 20), lastError: null,
    actions: [{ type: 'create_task', params: { title: 'Revisar documento subido por el cliente', dueInDays: 1 } }],
  },
  {
    id: 'w6', name: 'Campana AEP (manual)', triggerType: 'manual', isActive: true,
    runCount: 3, lastRunAt: at(-15, 14, 10), lastError: null,
    actions: [
      { type: 'send_email', params: { subject: 'AEP 2026 — revisemos tu plan antes del 7 de diciembre' } },
      { type: 'wait', params: { days: 3, hours: 0 } },
      { type: 'create_task', params: { title: 'Seguimiento AEP por telefono', dueInDays: 4 } },
    ],
  },
];

const sequences = [
  { id: 's1', name: 'Onboarding nuevo cliente', description: 'Seis pasos en tres semanas: bienvenida, portal de documentos, un SMS corto, como contactarnos, referidos y encuesta.', isActive: true, _count: { steps: 6, enrollments: 23 } },
  { id: 's2', name: 'Lead frio — reactivacion', description: 'Cuatro pasos en un mes —uno de ellos por SMS— para leads que pidieron cotizacion y no contestaron.', isActive: true, _count: { steps: 4, enrollments: 41 } },
  { id: 's3', name: 'Medicare AEP', description: 'Seis recordatorios de octubre a diciembre, con un SMS a mitad de plazo, y llamada a la accion para agendar.', isActive: true, _count: { steps: 6, enrollments: 88 } },
  { id: 's4', name: 'Pedir referidos', description: 'Dos correos a los 60 y 120 dias de la venta, solo a clientes activos sin reclamos abiertos.', isActive: false, _count: { steps: 2, enrollments: 12 } },
];

const sequenceSteps = {
  // Los nombres de los campos son los que lee la pantalla de verdad
  // (`position`, `dayOffset`, `subject`, `htmlBody`, `channel`, `smsBody`), no
  // unos parecidos: hasta el 2026-09-21 decian `order`/`delayDays`/`body` y el
  // editor los pintaba VACIOS — la tarjeta prometia "5 pasos" y dentro no
  // habia ninguno. Es la trampa del CLAUDE.md: un campo con otro nombre no da
  // error, se pierde en silencio.
  s1: [
    { id: 'st1', position: 0, dayOffset: 0, timeOfDay: '09:00', channel: 'email', subject: 'Bienvenido — esto es lo que sigue', htmlBody: '<p>Hola {{firstName}}, gracias por confiar en nosotros. En los proximos dias te iremos contando lo esencial.</p>' },
    { id: 'st2', position: 1, dayOffset: 2, timeOfDay: '09:00', channel: 'email', subject: 'Tu portal de documentos', htmlBody: '<p>Aqui subes tus documentos desde el telefono, sin escanear nada: {{portalLink}}</p>' },
    // El paso de SMS, para que en la demo se vea que una secuencia alterna.
    { id: 'st3', position: 2, dayOffset: 4, timeOfDay: '10:00', channel: 'sms', subject: 'Recordatorio corto por SMS', smsBody: 'Hola {{firstName}}, soy {{agentName}}. Cualquier duda con tu poliza, contesta a este mensaje.', htmlBody: '' },
    { id: 'st4', position: 3, dayOffset: 7, timeOfDay: '09:00', channel: 'email', subject: 'Como contactarnos cuando lo necesites', htmlBody: '<p>Puedes escribirnos por aqui mismo y te contesta tu agente, no un robot.</p>' },
    { id: 'st5', position: 4, dayOffset: 14, timeOfDay: '09:00', channel: 'email', subject: 'Conoces a alguien que necesite seguro?', htmlBody: '<p>Si nos refieres a un familiar o amigo, lo atendemos igual que a ti.</p>' },
    { id: 'st6', position: 5, dayOffset: 21, timeOfDay: '09:00', channel: 'email', subject: 'Como te fue con nosotros?', htmlBody: '<p>Nos ayudarias mucho con una resena. Se tarda un minuto.</p>' },
  ],
  s2: [
    { id: 'st7', position: 0, dayOffset: 0, timeOfDay: '09:00', channel: 'email', subject: 'Seguimos aqui con tu cotizacion', htmlBody: '<p>Hola {{firstName}}, te guardamos la cotizacion por si quieres repasarla.</p>' },
    { id: 'st8', position: 1, dayOffset: 5, timeOfDay: '10:00', channel: 'sms', subject: 'Toque corto por SMS', smsBody: 'Hola {{firstName}}, soy {{agentName}}. Sigue en pie tu cotizacion si te interesa.', htmlBody: '' },
    { id: 'st9', position: 2, dayOffset: 12, timeOfDay: '09:00', channel: 'email', subject: 'Tres dudas que nos hacen siempre', htmlBody: '<p>Deducible, red de medicos y que pasa si cambias de trabajo.</p>' },
    { id: 'st10', position: 3, dayOffset: 30, timeOfDay: '09:00', channel: 'email', subject: 'Cerramos tu cotizacion?', htmlBody: '<p>Si prefieres que la cerremos, contesta y la damos de baja. Sin compromiso.</p>' },
  ],
  s3: [
    { id: 'st11', position: 0, dayOffset: 0, timeOfDay: '09:00', channel: 'email', subject: 'Empieza el AEP: fechas que importan', htmlBody: '<p>Del 15 de octubre al 7 de diciembre puedes cambiar tu plan.</p>' },
    { id: 'st12', position: 1, dayOffset: 7, timeOfDay: '09:00', channel: 'email', subject: 'Tu plan cambia el ano que viene?', htmlBody: '<p>Revisemos juntos si te sigue conviniendo.</p>' },
    { id: 'st13', position: 2, dayOffset: 14, timeOfDay: '09:00', channel: 'email', subject: 'Agenda tu revision', htmlBody: '<p>Elige el hueco que mejor te venga.</p>' },
    { id: 'st14', position: 3, dayOffset: 30, timeOfDay: '10:00', channel: 'sms', subject: 'Aviso de fecha por SMS', smsBody: 'Hola {{firstName}}, quedan pocas semanas del AEP. Contesta y te agendamos.', htmlBody: '' },
    { id: 'st15', position: 4, dayOffset: 45, timeOfDay: '09:00', channel: 'email', subject: 'Ultimas dos semanas', htmlBody: '<p>Si no hacemos nada, tu plan se renueva como esta.</p>' },
    { id: 'st16', position: 5, dayOffset: 52, timeOfDay: '09:00', channel: 'email', subject: 'Se acaba el plazo', htmlBody: '<p>Ultimo aviso antes del 7 de diciembre.</p>' },
  ],
  s4: [
    { id: 'st17', position: 0, dayOffset: 60, timeOfDay: '09:00', channel: 'email', subject: 'Conoces a alguien a quien le sirva?', htmlBody: '<p>Si nos refieres a alguien, lo atendemos igual que a ti.</p>' },
    { id: 'st18', position: 1, dayOffset: 120, timeOfDay: '09:00', channel: 'email', subject: 'Seguimos aqui para los tuyos', htmlBody: '<p>Un familiar, un companero de trabajo, quien sea.</p>' },
  ],
};

const recurringInvoices = [
  {
    id: 'ri1', title: 'Prima mensual — Carmen Villanueva', clientId: 'c9',
    client: { id: 'c9', firstName: 'Carmen', lastName: 'Villanueva' },
    frequency: 'MONTHLY', intervalCount: 1, dayOfMonth: 1, dayOfWeek: null, timeOfDay: '08:00',
    nextRunAt: at(12, 8), lastRunAt: at(-18, 8), isActive: true,
    items: [{ description: 'ACA Silver Select PPO', qty: 1, rate: 185 }],
    discount: 0, discountType: 'amount', taxRate: 0,
  },
  {
    id: 'ri2', title: 'Prima mensual — James Thompson', clientId: 'c2',
    client: { id: 'c2', firstName: 'James', lastName: 'Thompson' },
    frequency: 'MONTHLY', intervalCount: 1, dayOfMonth: 5, dayOfWeek: null, timeOfDay: '08:00',
    nextRunAt: at(16, 8), lastRunAt: at(-14, 8), isActive: true,
    items: [{ description: 'Medigap Plan G', qty: 1, rate: 187 }],
    discount: 0, discountType: 'amount', taxRate: 0,
  },
  {
    id: 'ri3', title: 'Servicio anual — Ramirez Construction', clientId: 'c8',
    client: { id: 'c8', firstName: 'Diego', lastName: 'Ramirez' },
    frequency: 'YEARLY', intervalCount: 1, dayOfMonth: 15, dayOfWeek: null, timeOfDay: '09:00',
    nextRunAt: at(96, 9), lastRunAt: at(-269, 9), isActive: false,
    items: [{ description: 'Revision anual de poliza comercial', qty: 1, rate: 250 }],
    discount: 10, discountType: 'percent', taxRate: 0,
  },
];

const calendar = [
  { id: 'ev1', title: 'Cita con Maria Rodriguez — revision Medicare', description: 'Repasar las 3 opciones de la cotizacion Q-2601.', startTime: at(1, 10), endTime: at(1, 11), location: 'Oficina', type: 'appointment', clientId: 'c1', status: 'scheduled' },
  { id: 'ev2', title: 'Llamada Carmen Villanueva', description: 'Confirmar plan ACA para la familia.', startTime: at(2, 15, 30), endTime: at(2, 16), location: 'Telefono', type: 'call', clientId: 'c9', status: 'scheduled' },
  { id: 'ev3', title: 'Firma de documentos — Carlos Mendoza', description: 'SOA + consentimiento HIPAA.', startTime: at(3, 9), endTime: at(3, 10), location: 'Zoom', type: 'appointment', clientId: 'c6', status: 'scheduled' },
  { id: 'ev4', title: 'Cierre de comisiones del mes', description: 'Cruzar el archivo de Aetna y Humana.', startTime: at(6, 16), endTime: at(6, 18), location: '', type: 'deadline', clientId: '', status: 'scheduled' },
  { id: 'ev5', title: 'Cita con Ana Gutierrez — documentos pendientes', description: 'Falta verificacion de ingresos.', startTime: at(8, 11), endTime: at(8, 12), location: 'Oficina', type: 'appointment', clientId: 'c3', status: 'scheduled' },
  { id: 'ev6', title: 'Seguimiento Diego Ramirez', description: 'Recontactar antes de su renovacion comercial.', startTime: at(14, 14), endTime: at(14, 14, 30), location: 'Telefono', type: 'call', clientId: 'c8', status: 'scheduled' },
  { id: 'ev7', title: 'Cita cerrada — Patricia Williams', description: 'Paquete auto + hogar firmado.', startTime: at(-4, 13), endTime: at(-4, 14), location: 'Oficina', type: 'appointment', clientId: 'c7', status: 'completed' },
];

// "Citas" — the appointment-request module, distinct from the calendar events
// above (different endpoint, different row shape).
const citas = [
  { id: 'ct1', clientId: 'c1', clientName: 'Maria Rodriguez', clientEmail: 'm.rodriguez@example.com', policyType: 'ma', scheduledAt: at(1, 10), mode: 'in_person', status: 'confirmed', copyTo: 'sofia@example.com', notes: 'Traer tarjeta de Medicare y lista de medicamentos.' },
  { id: 'ct2', clientId: 'c9', clientName: 'Carmen Villanueva', clientEmail: 'cvillanueva@example.com', policyType: 'aca', scheduledAt: at(2, 15, 30), mode: 'phone', status: 'scheduled', copyTo: 'sofia@example.com', notes: 'Revisar ingreso del hogar para el subsidio.' },
  { id: 'ct3', clientId: 'c6', clientName: 'Carlos Mendoza', clientEmail: 'cmendoza@example.com', policyType: 'life', scheduledAt: at(3, 9), mode: 'video', status: 'scheduled', copyTo: 'sofia@example.com', notes: 'Term life 20 anos — definir beneficiario.' },
  { id: 'ct4', clientId: 'c3', clientName: 'Ana Gutierrez', clientEmail: 'anag@example.com', policyType: 'aca', scheduledAt: at(8, 11), mode: 'in_person', status: 'scheduled', copyTo: 'sofia@example.com', notes: 'Falta verificacion de ingresos.' },
  { id: 'ct5', clientId: 'c7', clientName: 'Patricia Williams', clientEmail: 'pwilliams@example.com', policyType: 'home', scheduledAt: at(-4, 13), mode: 'in_person', status: 'completed', copyTo: 'sofia@example.com', notes: 'Paquete auto + hogar firmado.' },
  { id: 'ct6', clientId: 'c5', clientName: 'Lucia Fernandez', clientEmail: 'luciaf@example.com', policyType: 'aca', scheduledAt: at(-2, 16), mode: 'phone', status: 'cancelled', copyTo: 'sofia@example.com', notes: 'Reagendar la proxima semana.' },
];

// BOOKING LINK — con la MISMA forma que manda el servidor (booking.routes.ts):
// la fila de BookingConfig tal cual, con los extras (lineas, descanso, días
// bloqueados, nombre del enlace…) DENTRO de `availability`. Antes era una forma
// vieja ({ mon: [{start,end}] }) y la pantalla salía con todos los días
// cerrados y las casillas vacías — justo lo que la guía del Centro de Ayuda
// tiene que enseñar (2026-10-29). Dos enlaces, como permite la pantalla: uno
// activo para Salud y Medicare y otro apagado para Vida.
// El n-ésimo día hábil desde hoy (lunes a viernes), para que las reservas de
// ejemplo caigan dentro del horario del enlace.
const diaHabil = (n) => {
  let d = 0;
  let vistos = 0;
  while (vistos < n) {
    d++;
    const dow = new Date(Date.now() + d * DAY).getDay();
    if (dow !== 0 && dow !== 6) vistos++;
  }
  return d;
};
const fechaDeDia = (dias) => {
  const d = new Date(Date.now() + dias * DAY);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const textosDelEnlace = () => (readLang() === 'en'
  ? { meetingTitle: 'Free insurance consultation', welcomeMessage: 'Pick the time that works best for you. We can meet by phone, video or at the office.' }
  : { meetingTitle: 'Consulta gratis de seguros', welcomeMessage: 'Escoge el horario que te quede mejor. Podemos hablar por teléfono, video o en la oficina.' });
const SEMANA_DEMO = {
  mon: { active: true, start: '09:00', end: '17:00', breakStart: '12:00', breakEnd: '13:00' },
  tue: { active: true, start: '09:00', end: '17:00', breakStart: '12:00', breakEnd: '13:00' },
  wed: { active: true, start: '09:00', end: '17:00', breakStart: '12:00', breakEnd: '13:00' },
  thu: { active: true, start: '09:00', end: '17:00', breakStart: '12:00', breakEnd: '13:00' },
  fri: { active: true, start: '09:00', end: '13:00' },
  sat: { active: false, start: '10:00', end: '14:00' },
  sun: { active: false, start: '', end: '' },
};
const bookingLinks = [
  {
    id: 'bc_demo_1', userId: 'u1', agencyId: 'a1', slug: 'sofia-marrero', isActive: true,
    durationMinutes: 30, timezone: 'America/New_York', advanceDays: 30, minNoticeHours: 2,
    availability: {
      ...SEMANA_DEMO,
      nombre: 'Salud y Medicare',
      lineas: ['salud', 'medicare'],
      bufferMinutes: 15,
      blockedDates: [fechaDeDia(diaHabil(8))],
      horasBloqueadas: { [fechaDeDia(diaHabil(5))]: { desde: '14:00', hasta: '16:00' } },
    },
  },
  {
    id: 'bc_demo_2', userId: 'u1', agencyId: 'a1', slug: 'sofia-marrero-vida', isActive: false,
    durationMinutes: 45, timezone: 'America/New_York', advanceDays: 30, minNoticeHours: 24,
    availability: { ...SEMANA_DEMO, nombre: 'Vida', lineas: ['vida'], bufferMinutes: 0 },
  },
];
const conTextos = (e) => ({ ...textosDelEnlace(), ...e });
// Guardar: el servidor mete los extras dentro de `availability`; aquí igual.
const guardarEnlaceDemo = (enlace, body) => {
  const { blockedDates, lineas, bufferMinutes, equipo, horarios, horasBloqueadas, nombre, apariencia, availability, ...resto } = body || {};
  Object.assign(enlace, resto);
  enlace.availability = {
    ...(availability || enlace.availability),
    blockedDates: blockedDates || [], lineas: lineas || [], bufferMinutes: Number(bufferMinutes) || 0,
    equipo: equipo || {}, horarios: horarios || {}, horasBloqueadas: horasBloqueadas || {}, nombre: nombre || '',
    // Color de la cabecera y ocultar el logo (E-404), igual que el servidor.
    apariencia: apariencia || (enlace.availability && enlace.availability.apariencia) || { color: null, ocultarLogo: false },
  };
  return enlace;
};

const bookingReservations = [
  { id: 'bk1', name: 'Rosa Delgado', email: 'rosa.delgado@example.com', phone: '305-555-0188', startTime: at(diaHabil(1), 11), endTime: at(diaHabil(1), 11, 30), status: 'confirmed', notes: '' },
  { id: 'bk2', name: 'Tony Beltran', email: 'tbeltran@example.com', phone: '813-555-0299', startTime: at(diaHabil(2), 14, 30), endTime: at(diaHabil(2), 15), status: 'confirmed', notes: '' },
  { id: 'bk3', name: 'Yolanda Prieto', email: 'yprieto@example.com', phone: '407-555-0344', startTime: at(diaHabil(4), 10), endTime: at(diaHabil(4), 10, 30), status: 'confirmed', notes: '' },
];

// Borradores de Correo Masivo: empieza vacio, se llena al guardar.
const campaignDrafts = [];

// ── router ───────────────────────────────────────────────────────────────────

// Solicitudes enviadas. El modulo salia vacio en la demo, asi que ni la lista
// ni la columna del ENLACE (que dice cuanto le queda antes de vencer, y de la
// que cuelga el boton de Reenviar) se veian nunca. Tres filas a proposito: una
// con el enlace vivo, una con el enlace ya VENCIDO —que es la que justifica el
// boton— y una ya completada, que no tiene enlace que vigilar.
const applications = [
  {
    id: 'ap_demo_1', recipientName: 'Carmen Delgado', recipientEmail: 'carmen.delgado@correo.com',
    policyType: 'HEALTH', status: 'VIEWED', language: 'es',
    sentAt: at(-1), viewedAt: at(-1, 18), submittedAt: null,
  },
  {
    id: 'ap_demo_2', recipientName: 'Rafael Ortiz', recipientEmail: 'rafael.ortiz@correo.com',
    policyType: 'LIFE', status: 'SENT', language: 'es',
    sentAt: at(-6), viewedAt: null, submittedAt: null, tokenRevokedAt: null,
  },
  {
    id: 'ap_demo_3', recipientName: 'Yolanda Prieto', recipientEmail: 'yolanda.prieto@correo.com',
    policyType: 'HEALTH', status: 'SUBMITTED', language: 'es',
    sentAt: at(-12), viewedAt: at(-12, 14), submittedAt: at(-11),
  },
];

// Una plantilla, para que la pestaña no diga "todavia no hay ninguna".
const applicationTemplates = [
  {
    id: 'apt_demo_1', name: 'Solicitud de salud (ACA)', policyType: 'HEALTH', language: 'es',
    intro: 'Completa tus datos para procesar tu inscripcion.',
    sections: [], customFields: [], isDefault: true,
  },
];

// Biblioteca de recursos + los enlaces del portal por cliente. El modulo salia
// vacio en la demo, asi que el boton "Generar enlace del portal" no se podia
// ver funcionar — y es justo el que cambio en la auditoria del 2026-08-23: el
// token lo acuña ahora el SERVIDOR y caduca.
const resources = [
  {
    id: 'res_demo_1', title: 'Guia de beneficios MegaSalud 2026', description: 'Resumen de coberturas y copagos.',
    type: 'pdf', policyType: 'HEALTH', carrier: 'MegaSalud', category: 'guias',
    fileUrl: '', fileName: 'guia-megasalud-2026.pdf', sharedWithAll: true, sharedWithClients: [],
    tags: ['salud', 'beneficios'], uploadedAt: dateOnly(-40),
  },
  {
    id: 'res_demo_2', title: 'Como usar tu tarjeta el primer mes', description: 'Video corto para clientes nuevos.',
    type: 'video', policyType: 'HEALTH', carrier: '', category: 'onboarding',
    fileUrl: 'https://youtu.be/dQw4w9WgXcQ', fileName: '', sharedWithAll: true, sharedWithClients: [],
    tags: ['onboarding'], uploadedAt: dateOnly(-12),
  },
];

// Empieza vacio a proposito: asi se ve el enlace NACER del lado del servidor.
const resourceTokens = [];

// ── Los medicos de la casa (punto 10, fase 1) ────────────────────────────────
//
// La pestaña "Medicos" de la ficha es de las que se piden al servidor, asi que
// en la demo salia vacia entera y con el aviso rojo de carga. Se contesta aqui,
// con medicos INVENTADOS: ni los nombres, ni los NPI, ni los telefonos son de
// nadie. Los NPI llevan 999 delante para que no puedan coincidir con uno real.
//
// `persona` son las claves de siempre ("primary", "spouse", el id del
// dependiente), las mismas que usa SEED_CLIENTS en deskte-full.jsx.
// Medicamentos de la demo: uno puesto para que la sección no salga vacía.
const clientMedications = [
  { id: 'med1', clientId: 'c1', persona: 'primary', nombre: 'metFORMIN (Oral Pill)', rxcui: '861007',
    dosis: '500 mg Tab', frecuencia: '2 veces al día', recetadoPor: '', notas: '',
    creadoPor: 'Lissette', actualizadoPor: 'Lissette', createdAt: '2026-09-01T15:00:00.000Z', updatedAt: '2026-09-01T15:00:00.000Z' },
];

// El catálogo del buscador en la demo, con la forma de la respuesta del servidor.
const CATALOGO_DEMO_MEDICAMENTOS = [
  { nombre: 'metFORMIN (Oral Pill)', presentaciones: [
    { dosis: '500 mg Tab', rxcui: '861007' }, { dosis: '850 mg Tab', rxcui: '861010' }, { dosis: '1000 mg Tab', rxcui: '861004' },
    { dosis: '500 mg 24 HR Extended Release Tab', rxcui: '860975' }] },
  { nombre: 'Glucophage (Oral Pill)', presentaciones: [{ dosis: '500 mg Tab', rxcui: '861008' }, { dosis: '1000 mg Tab', rxcui: '861005' }] },
  { nombre: 'Eliquis (Oral Pill)', presentaciones: [{ dosis: '2.5 mg Tab', rxcui: '1364441' }, { dosis: '5 mg Tab', rxcui: '1364447' }] },
  { nombre: 'atorvaSTATin (Oral Pill)', presentaciones: [
    { dosis: '10 mg Tab', rxcui: '617312' }, { dosis: '20 mg Tab', rxcui: '617310' }, { dosis: '40 mg Tab', rxcui: '617311' }, { dosis: '80 mg Tab', rxcui: '259255' }] },
  { nombre: 'lisinopril (Oral Pill)', presentaciones: [
    { dosis: '5 mg Tab', rxcui: '314076' }, { dosis: '10 mg Tab', rxcui: '314077' }, { dosis: '20 mg Tab', rxcui: '205326' }] },
  { nombre: 'Ozempic (Injectable)', presentaciones: [{ dosis: '0.25 mg or 0.5 mg/dose Pen Injector', rxcui: '2200650' }, { dosis: '1 mg/dose Pen Injector', rxcui: '2200655' }] },
  { nombre: 'insulin glargine (Injectable)', presentaciones: [{ dosis: '100 unt/mL Pen Injector', rxcui: '847232' }] },
];

// El directorio de médicos de la demo, con la forma de la respuesta del servidor.
const MEDICOS_DEMO_DIRECTORIO = [
  { npi: '9990000101', nombre: 'Ana Morales, MD', tipo: 'Individual', especialidades: ['Family Medicine'], direccion: '100 Demo Ave', ciudad: 'Miami', estado: 'FL', zip: '33012', telefono: '3055550101', distanciaMillas: 1.2, aceptaPacientes: true },
  { npi: '9990000202', nombre: 'Carlos Rivera, MD', tipo: 'Individual', especialidades: ['Cardiovascular Disease'], direccion: '200 Demo Blvd', ciudad: 'Hialeah', estado: 'FL', zip: '33012', telefono: '3055550202', distanciaMillas: 2.8, aceptaPacientes: true },
  { npi: '9990000303', nombre: 'Lucia Perez, MD', tipo: 'Individual', especialidades: ['Endocrinology, Diabetes & Metabolism'], direccion: '300 Demo St', ciudad: 'Miami', estado: 'FL', zip: '33125', telefono: '3055550303', distanciaMillas: 4.5, aceptaPacientes: false },
];

// La extension de WhatsApp: el estado del token y lo que ya guardo en la ficha.
// Pedro arranca con un token "activo" para que Ajustes enseñe el estado normal
// de una cuenta conectada; Generar/Revocar funcionan y se deshacen al recargar.
// Enlaces de HealthSherpa creados en la demo (se borran al recargar).
const hsSesiones = [];
// Notas de la agencia por hospital: en memoria, se pierden al recargar como todo lo de la demo.
const notasDeHospitalesDemo = {};

const SIN_TOKEN_WA = { hasToken: false, lastFour: null, lastUsedAt: null, createdAt: null, expiresAt: null, expired: false };
let waToken = { ...SIN_TOKEN_WA };

// Conversacion de coordinacion, como manda la regla de la extension: citas y
// documentos, nada medico. Las horas y la fecha son de hoy, en en-US.
const hoyEnUs = () => new Date().toLocaleDateString('en-US');
const msgWa = (i, dir, text, time) => ({ id: `wa_demo_${i}`, dir, text, time, date: hoyEnUs(), status: dir === 'out' ? 'sent' : 'read', _pasted: true });
// En el idioma de la demo (?lang=en): la guia en ingles sale de aqui.
const EN_DEMO = readLang() === 'en';
const pastedMessages = {
  'c11|whatsapp': EN_DEMO ? [
    msgWa(1, 'in', 'Good morning. Did you get the letter from the Marketplace?', '9:12 AM'),
    msgWa(2, 'out', 'Good morning, Pedro! Yes, we got it. We still need your most recent pay stub.', '9:15 AM'),
    msgWa(3, 'in', "Perfect, I'll send it this afternoon.", '9:16 AM'),
    msgWa(4, 'out', "Thanks. I booked you Thursday at 3:00 PM to go over the dental plan you asked about.", '9:18 AM'),
    msgWa(5, 'in', "Great, I'll be there 👍", '9:20 AM'),
  ] : [
    msgWa(1, 'in', 'Hola, buenos días. ¿Ya le llegó la carta del Mercado?', '9:12 AM'),
    msgWa(2, 'out', '¡Buenos días, Pedro! Sí, la recibimos. Le falta mandarnos el talón de pago más reciente.', '9:15 AM'),
    msgWa(3, 'in', 'Perfecto, se lo mando hoy por la tarde.', '9:16 AM'),
    msgWa(4, 'out', 'Gracias. Le dejo cita el jueves a las 3:00 PM para revisar el dental que pidió.', '9:18 AM'),
    msgWa(5, 'in', 'Listo, ahí estaré 👍', '9:20 AM'),
  ],
};

const clientDoctors = [
  {
    id: 'doc_demo_1', clientId: 'c1', persona: 'primary',
    nombre: 'Dra. Alicia Morales, MD', especialidad: 'PCP', npi: '9990012345',
    centro: 'Hialeah Family Health', direccion: '1450 W 49th St, Hialeah, FL 33012',
    telefono: '(305) 555-0180', fax: '(305) 555-0181', red: 'Florida Blue HMO',
    notas: 'Habla espanol. Cita de seguimiento cada 3 meses por la diabetes.',
    origen: 'Orden de referido del 02/10/2026',
    creadoPor: 'Lissette', actualizadoPor: 'Lissette',
    createdAt: at(-40), updatedAt: at(-40),
  },
  {
    id: 'doc_demo_2', clientId: 'c1', persona: 'd10',
    nombre: 'Dr. Peter Nolan, DO', especialidad: 'Pediatria', npi: '9990067890',
    centro: 'Palm Springs Pediatrics', direccion: '2601 W 68th St, Hialeah, FL 33016',
    telefono: '(305) 555-0192', fax: '', red: 'Florida Blue HMO', notas: '',
    origen: 'Lo dijo la clienta por telefono',
    creadoPor: 'Lissette', actualizadoPor: 'Lissette',
    createdAt: at(-12), updatedAt: at(-12),
  },
  {
    id: 'doc_demo_3', clientId: 'c9', persona: 'spouse',
    nombre: 'Dr. Hector Salas, MD', especialidad: 'Cardiologia', npi: '9990054321',
    centro: 'North Miami Heart Group', direccion: '13100 NW 7th Ave, Miami, FL 33168',
    telefono: '(305) 555-0210', fax: '(866) 555-0211', red: 'Ambetter',
    notas: 'Referido por su PCP en marzo.', origen: 'Carta de la aseguradora',
    creadoPor: 'Lissette', actualizadoPor: 'Lissette',
    createdAt: at(-60), updatedAt: at(-3),
  },
];

// ── Las citas medicas (punto 10, fase 2) ─────────────────────────────────────
//
// Mismo motivo que los medicos: el bloque de la ficha y la lista global de
// Tareas -> Citas medicas se piden al servidor, asi que en la demo salian
// vacias y con el aviso rojo. Todo inventado; los NPI empiezan por 999.
const citasMedicas = [
  {
    id: 'cita_demo_1', clientId: 'c1', persona: 'primary', doctorId: 'doc_demo_1',
    startsAt: at(4, 9, 30), status: 'confirmada',
    orden: 'REF-2026-00841',
    razon: 'Chequeo de seguimiento de la diabetes. Llevar la lista de medicamentos. Llegar 20 minutos antes para el registro.',
    dir1: '1450 W 49th St', dir2: 'Suite 210', ciudad: 'Hialeah', estado: 'FL', zip: '33012', condado: 'Miami-Dade',
    notas: '', proveedor: { nombre: 'Dra. Alicia Morales, MD', especialidad: 'PCP', npi: '9990012345', telefono: '(305) 555-0180', origen: 'medico' },
    direccion: '1450 W 49th St, Suite 210, Hialeah, FL 33012',
    confirmadoPor: 'Lissette', confirmedAt: at(-2), enElCalendario: false,
    creadoPor: 'Lissette', actualizadoPor: 'Lissette', createdAt: at(-9), updatedAt: at(-2),
  },
  {
    id: 'cita_demo_2', clientId: 'c1', persona: 'd10', doctorId: 'doc_demo_2',
    startsAt: at(18, 15, 0), status: 'pendiente',
    orden: '', razon: 'Vacunas del colegio.',
    dir1: '2601 W 68th St', dir2: '', ciudad: 'Hialeah', estado: 'FL', zip: '33016', condado: 'Miami-Dade',
    notas: '', proveedor: { nombre: 'Dr. Peter Nolan, DO', especialidad: 'Pediatria', npi: '9990067890', telefono: '(305) 555-0192', origen: 'medico' },
    direccion: '2601 W 68th St, Hialeah, FL 33016',
    confirmadoPor: '', confirmedAt: null, enElCalendario: false,
    creadoPor: 'Lissette', actualizadoPor: 'Lissette', createdAt: at(-1), updatedAt: at(-1),
  },
  {
    id: 'cita_demo_3', clientId: 'c9', persona: 'spouse', doctorId: 'doc_demo_3',
    startsAt: at(-6, 11, 0), status: 'completada',
    orden: 'REF-2026-00712', razon: 'Prueba de esfuerzo. El resultado lo manda la clinica por el portal.',
    dir1: '13100 NW 7th Ave', dir2: '', ciudad: 'Miami', estado: 'FL', zip: '33168', condado: 'Miami-Dade',
    notas: 'Fue acompanado por su esposa.',
    proveedor: { nombre: 'Dr. Hector Salas, MD', especialidad: 'Cardiologia', npi: '9990054321', telefono: '(305) 555-0210', origen: 'medico' },
    direccion: '13100 NW 7th Ave, Miami, FL 33168',
    confirmadoPor: 'Lissette', confirmedAt: at(-10), enElCalendario: true,
    creadoPor: 'Lissette', actualizadoPor: 'Lissette', createdAt: at(-20), updatedAt: at(-6),
  },
  {
    id: 'cita_demo_4', clientId: 'c9', persona: 'd2', doctorId: null,
    startsAt: at(9, 8, 15), status: 'pendiente',
    orden: '', razon: 'Limpieza dental de rutina.',
    dir1: '820 NE 125th St', dir2: '', ciudad: 'North Miami', estado: 'FL', zip: '33161', condado: 'Miami-Dade',
    notas: '', proveedor: { nombre: 'Sunrise Dental Care', especialidad: 'Dental', npi: '', telefono: '(305) 555-0244', origen: 'suelto' },
    direccion: '820 NE 125th St, North Miami, FL 33161',
    confirmadoPor: '', confirmedAt: null, enElCalendario: false,
    creadoPor: 'Lissette', actualizadoPor: 'Lissette', createdAt: at(-3), updatedAt: at(-3),
  },
];

// El proveedor sale del MEDICO cuando la cita cuelga de uno, igual que en el
// servidor de verdad; si no, de lo que se escribio a mano.
function proveedorDeLaDemo(body, anterior) {
  const m = body?.doctorId ? clientDoctors.find((d) => d.id === body.doctorId) : null;
  if (m) return { nombre: m.nombre, especialidad: m.especialidad, npi: m.npi, telefono: m.telefono, origen: 'medico' };
  const nombre = (body?.proveedorNombre || '').trim();
  if (!nombre) return anterior?.proveedor || { nombre: '', especialidad: '', npi: '', telefono: '', origen: 'vacio' };
  return {
    nombre, especialidad: body?.proveedorEspecialidad || '', npi: body?.proveedorNpi || '',
    telefono: body?.proveedorTelefono || '', origen: 'suelto',
  };
}

// El nombre del cliente lo pone el SERVIDOR en la lista global; aqui igual.
const NOMBRES_DE_LA_DEMO = { c1: 'Maria Rodriguez', c9: 'Carmen Villanueva' };

const ok = (body) => new Response(JSON.stringify(body), {
  status: 200, headers: { 'Content-Type': 'application/json' },
});
const notFound = () => new Response(JSON.stringify({ error: 'Not found' }), {
  status: 404, headers: { 'Content-Type': 'application/json' },
});

const parseBody = (init) => {
  try { return init && init.body ? JSON.parse(init.body) : {}; } catch { return {}; }
};

const removeById = (arr, id) => {
  const i = arr.findIndex((x) => x.id === id);
  if (i < 0) return false;
  arr.splice(i, 1);
  return true;
};

// One collection's worth of REST: list / create / read / update / delete, plus
// the pause & resume pair that workflows and recurring invoices share.
function collection(arr, prefix, decorate) {
  return (rest, method, body) => {
    // rest is what follows the collection path: '' | '/<id>' | '/<id>/<action>'
    const [, id, action] = rest.split('/');

    if (!id) {
      if (method === 'GET') return ok({ data: arr });
      if (method === 'POST') {
        const created = { id: newId(prefix), createdAt: new Date().toISOString(), ...body };
        if (decorate) decorate(created, body);
        arr.unshift(created);
        return ok({ data: created });
      }
      return null;
    }

    const idx = arr.findIndex((x) => x.id === id);
    const row = idx >= 0 ? arr[idx] : null;

    if (!action) {
      if (!row) return notFound();
      if (method === 'GET') return ok({ data: row });
      if (method === 'PUT' || method === 'PATCH') {
        arr[idx] = { ...row, ...body, id: row.id };
        if (decorate) decorate(arr[idx], body);
        return ok({ data: arr[idx] });
      }
      if (method === 'DELETE') { removeById(arr, id); return ok({ data: true }); }
      return null;
    }

    if (!row) return notFound();
    if (action === 'pause')  { row.isActive = false; return ok({ data: row }); }
    if (action === 'resume') { row.isActive = true;  return ok({ data: row }); }
    return null;
  };
}

// Plantillas de firma. En el CRM las trae GET /doc-templates y el propio CRM le
// siembra a cada agencia las del sistema; la demo no pasaba por ahi, asi que
// Firmas salia SIN una sola plantilla — el modulo entero se veia vacio en una
// pagina de ventas. Estas tres son cortas y de mentira a proposito: ensenan que
// el documento se llena con los datos del cliente ({nombre}, {agente}, {fecha}),
// que es lo que se viene a ver, sin publicar el texto legal de la agencia.
const docTemplates = [
  {
    id: 'ct_demo_1', category: 'consent', source: 'system', createdAt: dateOnly(-120),
    name: 'Consentimiento ACA / Marketplace (demo)', nameEn: 'ACA / Marketplace Consent (demo)',
    bodyEs: 'CONSENTIMIENTO DE INSCRIPCION — MERCADO DE SALUD (ACA)\n\nYo, {nombre}, autorizo a {agente} de {agency} a gestionar mi inscripcion y mi cobertura en el Mercado de Salud.\n\nEntiendo que firmar esto NO me obliga a inscribirme en ningun plan: la decision final es mia.\n\nTelefono: {telefono}    Correo: {email}\nFecha: {fecha}\nCliente: {nombre}\n\n(Texto de ejemplo de la demo. Su agencia usa el suyo.)',
    bodyEn: 'MARKETPLACE ENROLLMENT CONSENT — ACA\n\nI, {nombre}, authorize {agente} of {agency} to handle my enrollment and coverage in the Health Insurance Marketplace.\n\nI understand that signing this does NOT commit me to any plan: the final decision is mine.\n\nPhone: {telefono}    Email: {email}\nDate: {fecha}\nClient: {nombre}\n\n(Demo sample text. Your agency uses its own.)',
  },
  {
    id: 'ct_demo_2', category: 'hipaa', source: 'system', createdAt: dateOnly(-120),
    name: 'Autorizacion HIPAA (demo)', nameEn: 'HIPAA Authorization (demo)',
    bodyEs: 'AUTORIZACION PARA DIVULGAR INFORMACION DE SALUD (HIPAA)\n\nYo, {nombre}, nacido el {dob}, autorizo a {agency} a recibir y usar mi informacion de salud unicamente para gestionar mi cobertura.\n\nPuedo revocar esta autorizacion por escrito en cualquier momento.\n\nFecha: {fecha}\nCliente: {nombre}\n\n(Texto de ejemplo de la demo. Su agencia usa el suyo.)',
    bodyEn: 'AUTHORIZATION TO DISCLOSE HEALTH INFORMATION (HIPAA)\n\nI, {nombre}, born {dob}, authorize {agency} to receive and use my health information solely to manage my coverage.\n\nI may revoke this authorization in writing at any time.\n\nDate: {fecha}\nClient: {nombre}\n\n(Demo sample text. Your agency uses its own.)',
  },
  {
    id: 'ct_demo_3', category: 'soa', source: 'system', createdAt: dateOnly(-120),
    name: 'Alcance de la cita — Medicare (SOA, demo)', nameEn: 'Scope of Appointment — Medicare (SOA, demo)',
    bodyEs: 'ALCANCE DE LA CITA (SCOPE OF APPOINTMENT) — MEDICARE\n\nYo, {nombre}, acepto que en la cita con {agente} se hablen los productos de Medicare marcados en este formulario.\n\nFirmar esto NO es una solicitud de inscripcion en ningun plan.\n\nFecha: {fecha}\nCliente: {nombre}\n\n(Texto de ejemplo de la demo. Su agencia usa el suyo.)',
    bodyEn: 'SCOPE OF APPOINTMENT — MEDICARE\n\nI, {nombre}, agree that the Medicare products checked on this form may be discussed at my appointment with {agente}.\n\nSigning this is NOT an enrollment request for any plan.\n\nDate: {fecha}\nClient: {nombre}\n\n(Demo sample text. Your agency uses its own.)',
  },
];
const docTemplatesRoute = collection(docTemplates, 'ct');

// ── Chat del equipo ────────────────────────────────────────────────────────
// El modulo entero estaba muerto en la demo (no hay servidor, y el chat lo pide
// TODO al servidor): se veia el error rojo y ya. Aqui van los canales, unos
// mensajes sembrados y las reglas de avisos, que es justo lo que un visitante
// quiere mirar: que el CRM avisa solo en el canal que toca.
//
// Los canales de POLIZAS son los que crea de verdad el reparto por ramo
// (Lissette 2026-09-14): la poliza nueva cae en el canal de su tipo.
const canalesDeChat = [
  { key: 'general', name: 'General', type: 'general' },
  { id: 'ch_pros', key: 'prospectos-y-referidos', name: 'Prospectos y Referidos', type: 'topic' },
  { id: 'ch_aca', key: 'polizas-aca-marketplace', name: 'Pólizas: ACA / Marketplace', type: 'topic' },
  { id: 'ch_life', key: 'polizas-seguro-de-vida', name: 'Pólizas: Seguro de Vida', type: 'topic' },
];
const delBot = (id, channel, body, linkTo, hace) => ({
  id, channel, body, linkTo, createdAt: at(hace), editedAt: null, deletedAt: null,
  attachmentUrl: null, attachmentMime: null, attachmentName: null, parentId: null,
  authorId: null, author: null, isSystem: true, deleted: false, reactions: [],
  replyCount: 0, lastReplyAt: null,
});
const mensajesDeChat = {
  general: [
    delBot('cm1', 'general', '✅ Cotización aceptada: Carlos Mendoza · Q-2603', '/quotes', -2),
    delBot('cm2', 'general', '✍️ Documento firmado: Ana Gutierrez · Consentimiento ACA', '/clients/c2', -1),
  ],
  'prospectos-y-referidos': [
    delBot('cm3', 'prospectos-y-referidos', '🌱 Prospecto nuevo: Lucia Fernandez', '/clients/c4', -1),
  ],
  'polizas-aca-marketplace': [
    delBot('cm4', 'polizas-aca-marketplace', '🛡️ Póliza nueva: Carmen Villanueva · ACA / Marketplace (Florida Blue)', '/clients/c9', -1),
    delBot('cm5', 'polizas-aca-marketplace', '🛡️ 12 póliza(s) de ACA / Marketplace importadas', '/policies', 0),
  ],
  'polizas-seguro-de-vida': [
    delBot('cm6', 'polizas-seguro-de-vida', '🛡️ Póliza nueva: Barbara Mitchell · Seguro de Vida (Mutual of Omaha)', '/clients/c3', 0),
  ],
};
// Encendidos los que tienen mensajes sembrados, para que lo que se ve en los
// canales y lo que dice la pantalla de ajustes cuadren.
const reglasDeChat = [
  { event: 'policy.created', enabled: true, channelKey: 'general' },
  { event: 'policy.created.por_ramo', enabled: true, channelKey: 'general' },
  { event: 'client.prospect', enabled: true, channelKey: 'prospectos-y-referidos' },
  { event: 'quote.accepted', enabled: true, channelKey: 'general' },
  { event: 'signature.signed', enabled: true, channelKey: 'general' },
];

const quotesRoute = collection(quotes, 'q', (row, body) => {
  row.number = row.number || `Q-26${String(90 + idSeq).slice(-2)}`;
  row.status = row.status || 'DRAFT';
  row.items = Array.isArray(body.items)
    ? body.items.map((it, i) => ({ id: `${row.id}_i${i}`, ...it }))
    : (row.items || []);
});
const workflowsRoute = collection(workflows, 'w', (row) => {
  if (row.isActive === undefined) row.isActive = true;
  row.runCount = row.runCount || 0;
  row.lastRunAt = row.lastRunAt || null;
});
const sequencesRoute = collection(sequences, 's', (row, body) => {
  if (row.isActive === undefined) row.isActive = true;
  row._count = row._count || { steps: (body.steps || []).length, enrollments: 0 };
});
const recurringRoute = collection(recurringInvoices, 'ri', (row) => {
  if (row.isActive === undefined) row.isActive = true;
  row.nextRunAt = row.nextRunAt || at(30, 8);
  row.items = row.items || [];
});
const calendarRoute = collection(calendar, 'ev');
const citasRouteBase = collection(citas, 'ct', (row) => {
  row.status = row.status || 'scheduled';
  row.mode = row.mode || 'in_person';
});

// ── Una cita agendada SE VE en el calendario ────────────────────────────────
//
// En el servidor, crear una cita escribe tambien su fila del calendario
// (`appointments.routes.ts`, `source = 'cita'`), que es justo lo que faltaba:
// *"no coloca la cita en el calendario"* (Lissette, 2026-09-21). Las citas
// SEMBRADAS de aqui arriba ya venian con su evento a mano (ct1↔ev1, ct2↔ev2...),
// asi que el unico caso que se quedaba fuera era el que mas importa en una
// demostracion: la cita que el visitante acaba de crear.
//
// El espejo se guarda por `citaId` y no por el id del evento, para que
// re-agendar actualice el mismo bloque y borrar se lo lleve — igual que en el
// servidor, donde eso lo garantiza `(source, sourceRef)`.
const MODO_A_TIPO = { in_person: 'in-person', phone: 'phone', video: 'video' };
const MODO_TEXTO = { in_person: 'En persona', phone: 'Llamada', video: 'Video' };

// La nota, sin el formato con el que se guarda. El editor escribe `**negrita**`
// y `==resaltado==`, o HTML acotado tras el centinela `<!--dkt-rich-->`; un
// bloque del calendario es texto pelado, asi que las marcas se quitan o salen
// impresas — que es justo lo que hacia el correo del servidor antes de E-305.
// Copia deliberada de `deskte-api/src/utils/formatoDeNota.ts`: es servidor, y
// este modulo no importa nada (ver la nota de TRADUCCIONES_DE_LA_DEMO).
function notaEnTextoPlano(nota) {
  let s = String(nota ?? '');
  if (!s) return '';
  if (s.startsWith('<!--dkt-rich-->')) {
    s = s.slice('<!--dkt-rich-->'.length)
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\s*\/\s*(div|p|li|tr|h[1-6])\s*>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"').replace(/&#0*39;|&apos;/gi, "'");
  } else {
    s = s.replace(/\*\*([\s\S]+?)\*\*/g, '$1').replace(/==([\s\S]+?)==/g, '$1');
  }
  return s.replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function eventoDeCita(cita) {
  const inicio = new Date(cita.scheduledAt);
  if (isNaN(inicio.getTime())) return null;
  const minutos = Number(cita.durationMin) > 0 ? Number(cita.durationMin) : 60;
  const quien = cita.clientName || 'Cliente';
  const detalle = [
    cita.clientEmail ? `Correo / Email: ${cita.clientEmail}` : '',
    cita.clientPhone ? `Teléfono / Phone: ${cita.clientPhone}` : '',
    MODO_TEXTO[cita.mode] ? `Modalidad / Mode: ${MODO_TEXTO[cita.mode]}` : '',
    notaEnTextoPlano(cita.notes),
  ].filter(Boolean).join('\n');
  return {
    // Sin el nombre del ramo a proposito: traerlo obligaria a importar
    // `policyTypes.js` aqui, y este modulo no importa nada (ver la nota de
    // TRADUCCIONES_DE_LA_DEMO). El servidor si lo pone; la demo no lo necesita.
    title: `Cita: ${quien}`,
    description: detalle || null,
    startTime: inicio.toISOString(),
    endTime: new Date(inicio.getTime() + minutos * 60000).toISOString(),
    location: cita.location || '',
    type: MODO_A_TIPO[cita.mode] || 'in-person',
    status: cita.status === 'cancelled' ? 'cancelled' : 'scheduled',
    clientId: cita.clientId || '',
    citaId: cita.id,
  };
}

function espejarCitaEnCalendario(cita) {
  const datos = eventoDeCita(cita);
  if (!datos) return;
  const i = calendar.findIndex((e) => e.citaId === cita.id);
  if (i >= 0) calendar[i] = { ...calendar[i], ...datos };
  else calendar.unshift({ id: newId('ev'), ...datos });
}

function citasRoute(rest, method, body) {
  const res = citasRouteBase(rest, method, body);
  if (!res) return res;
  const [, id] = rest.split('/');
  if (method === 'DELETE' && id) {
    const i = calendar.findIndex((e) => e.citaId === id);
    if (i >= 0) calendar.splice(i, 1);
  } else if (method === 'PUT' || method === 'PATCH') {
    const cita = citas.find((x) => x.id === id);
    if (cita) espejarCitaEnCalendario(cita);
  } else if (method === 'POST' && !id) {
    // `collection` hace `unshift`, asi que la recien creada es la primera.
    if (citas[0]) espejarCitaEnCalendario(citas[0]);
  }
  return res;
}
// Paneles de Reportes. Uno sembrado con la plantilla profesional, para que la
// demo enseñe el tablero lleno (las gráficas de pólizas se calculan con las
// pólizas SEED_* de la demo, sin servidor). Se pueden crear y editar más; se
// borran al recargar, como todo lo de aquí.
let idDeGrafica = 0;
const dashboards = [{
  id: 'dash_demo_1',
  name: 'Panel de ventas',
  widgets: plantillaProfesional(() => `wdemo${++idDeGrafica}`),
  filters: { datePreset: 'all', refreshMinutes: 5 },
  sharedWithAgency: true,
  isOwner: true,
  createdAt: at(-20),
  updatedAt: at(-1),
}];
const dashboardsRoute = collection(dashboards, 'dash', (row) => { row.isOwner = true; });

const invoicesRoute = collection(invoices, 'inv', (row) => {
  row.number = row.number || `INV-26${String(90 + idSeq).slice(-2)}`;
  row.items = row.items || [];
  row.payments = row.payments || [];
});

// Los cuatro textos de fabrica del mensaje del enlace de renovacion, en los
// dos sentidos. Es una copia a proposito: demoApi.js no puede importar de
// mensajeDelEnlaceDeRenovacion.js sin arrastrar ese modulo a la demo por una
// tabla de cuatro lineas, y si algun dia el texto de fabrica cambia lo unico
// que pasa aqui es que el boton cae al rechazo de siempre.
const TRADUCCIONES_DE_LA_DEMO = {
  'Hola {nombre}, soy {agente}. Te preparé la revisión de tu renovación. ¿Quieres verla? Respóndeme SÍ.':
    "Hi {nombre}, this is {agente}. I've prepared your renewal review. Want to see it? Reply YES.",
  "Hi {nombre}, this is {agente}. I've prepared your renewal review. Want to see it? Reply YES.":
    'Hola {nombre}, soy {agente}. Te preparé la revisión de tu renovación. ¿Quieres verla? Respóndeme SÍ.',
  'Hola {nombre}, aquí está tu revisión de renovación: {enlace} — revisa tus datos y dime si todo está correcto. — {agente}':
    "Hi {nombre}, here's your renewal review: {enlace} — please check your info and let me know if it's all correct. — {agente}",
  "Hi {nombre}, here's your renewal review: {enlace} — please check your info and let me know if it's all correct. — {agente}":
    'Hola {nombre}, aquí está tu revisión de renovación: {enlace} — revisa tus datos y dime si todo está correcto. — {agente}',
};

// Catálogo del cotizador guiado (docs/PLAN-cotizador-guiado.md, Fase 1).
//
// Los de la "agencia" son INVENTADOS a propósito: la demo es pública y ni las
// tarifas de un carrier real ni la tabla de disponibilidad que una agencia
// compiló para sí deben salir aquí. Los precios están redondeados para que
// nadie los tome por una cotización.
//
// La única excepción son los 4 productos de UnitedHealthcare de abajo: sus
// NOMBRES y beneficios ya eran públicos (catalogoPlanesUhc.js), y van SIN precios.
// Son una copia de DEMOSTRACIÓN —el catálogo base real vive en el servidor,
// deskte-api/src/data/catalogoBase.ts, con estados y datos que aquí no están—.
//
// Todo se arma la PRIMERA vez que la demo lo pide (catalogoDemo()): nada se
// calcula al cargar el módulo, así esta lista no acaba en el bundle de
// app.deskte.com, donde la demo es código muerto.
const CATEGORIA_UHC = { majorMedical: 'short_term', indemnity: 'indemnity' };
const textoUhc = (v) => (v && typeof v === 'object' ? v.es : (v || ''));
const productosBaseUhcDemo = () => PRODUCTOS_UHC.map((fam) => {
  const planes = PLANES_UHC.filter((x) => x.familia === fam).map((x) => x.plan);
  return {
    id: `base-uhc-${fam.producto.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    origen: 'deskte', nombre: fam.producto, compania: UHC_CARRIER,
    categoria: CATEGORIA_UHC[fam.estilo] || 'otro', activo: true,
    estadosModo: 'todos', estados: [], estadosPorConfirmar: true,
    edadMin: 18, edadMax: 64, underwriting: 'con', preexistencias: '',
    esperas: textoUhc(fam.comun.waitingPeriods),
    beneficios: [textoUhc(fam.resumen), textoUhc(fam.comun.planWarnings)].filter(Boolean).join('\n'),
    notasInternas: `Planes: ${planes.join(', ')}. ${textoUhc(fam.comun.extraCosts)}`.trim(),
    comision: '', cuotaInscripcion: '',
    tarifa: { modo: 'manual', porEdad: false, planes, filas: [] },
    requisitos: [], brochures: [],
    // El portal público de UHC, el mismo que el catálogo base (botón "Cotizar").
    enlaceCotizador: 'https://www.uhone.com/',
  };
});
const BANDAS_DE_EDAD = [[19, 29], [30, 39], [40, 49], [50, 59], [60, 64]];
const coberturaPorEdad = (planes, base, subePorBanda) => BANDAS_DE_EDAD.map(([desde, hasta], i) => ({
  desde, hasta,
  precios: Object.fromEntries(planes.map((pl, j) => {
    const v = base * (1 + j * 0.35) + subePorBanda * i;
    return [pl, { personal: Math.round(v), pareja: Math.round(v * 2), hijos: Math.round(v * 1.8), familia: Math.round(v * 2.7) }];
  })),
}));
// Los 4 de UHC con los nombres de los PDF reales de C:\Deskte\catalogo, para que
// la carga de una vez de los brochures base se pueda probar con esos archivos.
// Los PDF que se suban en la demo NO salen del navegador (brochuresDeLaDemo).
const PDF_UHC = {
  'base-uhc-triterm-medical': { brochures: ['Triterm-Medical-Plans.pdf'], materialAgente: ['PREGUNTAS MEDICAS - 1.pdf', 'PREGUNTAS MEDICAS - 2.pdf', 'TABLA DE PESO Y ESTATURA.pdf'] },
  'base-uhc-short-term-medical': { brochures: ['SHORT TERM BROCHURE.pdf'], materialAgente: [] },
  'base-uhc-health-protectorguard': { brochures: ['HEALTH PROTECTOR GUARD.pdf', 'HEALTH PROTECTOR GUARD BROCHURE ESPAÑOL 1 (1).pdf'], materialAgente: [] },
  'base-uhc-guard': { brochures: ['Guard-Plans.pdf'], materialAgente: [] },
};
// Cuánto paga cada plan y qué no cubre de los 4 de UHC (los mismos datos que
// el catálogo base del servidor; beneficios públicos, sin precios). Se arma
// solo cuando la demo lo pide, como todo lo de arriba.
const coberturasUhcDemo = () => ({"base-uhc-guard": {"coberturas": {"tipo": "indemnizacion", "filas": [{"concepto": "Primer día de hospital (admisión)", "valores": {"Guard 4000": "$4,000 por día (máx. 1 día)", "Guard 5000": "$5,000 por día (máx. 1 día)", "Guard 6000": "$6,000 por día (máx. 1 día)"}}, {"concepto": "Hospital (por día)", "valores": {"Guard 4000": "Año 1: $100 por día (máx. 31 días); año 2: $200 por día (máx. 31 días; el aumento no aplica a internación por enfermedad)", "Guard 5000": "Año 1: $100 por día (máx. 31 días); año 2: $200 por día (máx. 31 días; el aumento no aplica a internación por enfermedad)", "Guard 6000": "Año 1: $100 por día (máx. 31 días); año 2: $200 por día (máx. 31 días; el aumento no aplica a internación por enfermedad)"}}, {"concepto": "Sala de emergencia", "valores": {"Guard 4000": "$300 por día (máx. 3 días)", "Guard 5000": "$500 por día (máx. 3 días)", "Guard 6000": "$600 por día (máx. 3 días)"}}, {"concepto": "Consultas en consultorio (máximo combinado)", "valores": {"Guard 4000": "Año 1: 4 visitas; año 2: 6 visitas", "Guard 5000": "Año 1: 4 visitas; año 2: 6 visitas", "Guard 6000": "Año 1: 4 visitas; año 2: 6 visitas"}}, {"concepto": "Consulta médica", "valores": {"Guard 4000": "$80 por visita", "Guard 5000": "$100 por visita", "Guard 6000": "$120 por visita"}}, {"concepto": "Especialista", "valores": {"Guard 4000": "$100 por visita", "Guard 5000": "$120 por visita", "Guard 6000": "$140 por visita"}}, {"concepto": "Urgencias (urgent care)", "valores": {"Guard 4000": "$100 por visita", "Guard 5000": "$150 por visita", "Guard 6000": "$200 por visita"}}, {"concepto": "Cirugía (7 niveles)", "valores": {"Guard 4000": "$125-$12,500 por día (máx. 2 días)", "Guard 5000": "$125-$12,500 por día (máx. 2 días)", "Guard 6000": "$250-$25,000 por día (máx. 2 días)"}}, {"concepto": "Cirugía ambulatoria (centro ambulatorio)", "valores": {"Guard 4000": "$1,000 por día (máx. 2 días)", "Guard 5000": "$1,500 por día (máx. 2 días)", "Guard 6000": "$2,000 por día (máx. 2 días)"}}, {"concepto": "Visita del médico en el hospital", "valores": {"Guard 4000": "$100 por día (máx. 1 visita por día)", "Guard 5000": "$100 por día (máx. 1 visita por día)", "Guard 6000": "$100 por día (máx. 1 visita por día)"}}, {"concepto": "Ambulancia terrestre/acuática", "valores": {"Guard 4000": "$500 por viaje (máx. 1 viaje de cualquier tipo)", "Guard 5000": "$600 por viaje (máx. 1 viaje de cualquier tipo)", "Guard 6000": "$600 por viaje (máx. 1 viaje de cualquier tipo)"}}, {"concepto": "Ambulancia aérea", "valores": {"Guard 4000": "$3,000 por viaje (máx. 1 viaje de cualquier tipo)", "Guard 5000": "$3,000 por viaje (máx. 1 viaje de cualquier tipo)", "Guard 6000": "$3,000 por viaje (máx. 1 viaje de cualquier tipo)"}}, {"concepto": "Rayos X ambulatorio", "valores": {"Guard 4000": "No cubre", "Guard 5000": "$50 por prueba (máx. 4 pruebas combinadas)", "Guard 6000": "$100 por prueba (máx. 4 pruebas combinadas)"}}, {"concepto": "Diagnóstico nivel 1 (ultrasonido, EKG, EEG, angiograma...)", "valores": {"Guard 4000": "No cubre", "Guard 5000": "$250 por prueba", "Guard 6000": "$500 por prueba"}}, {"concepto": "MRI / PET / CAT", "valores": {"Guard 4000": "No cubre", "Guard 5000": "$500 por prueba", "Guard 6000": "$500 por prueba"}}, {"concepto": "Medicinas", "valores": {"Guard 4000": "No cubre", "Guard 5000": "Genérico $10 y marca $40 por receta; año 1: 10 recetas, año 2: 15 recetas", "Guard 6000": "Genérico $10 y marca $40 por receta; año 1: 10 recetas, año 2: 15 recetas"}}, {"concepto": "Terapias (quiropráctico, física, ocupacional, del habla)", "valores": {"Guard 4000": "$25 por visita (máx. 10 visitas)", "Guard 5000": "$35 por visita (máx. 10 visitas)", "Guard 6000": "$50 por visita (máx. 10 visitas)"}}, {"concepto": "Telemedicina (HealthiestYou)", "valores": {"Guard 4000": "$0, visitas ilimitadas", "Guard 5000": "$0, visitas ilimitadas", "Guard 6000": "$0, visitas ilimitadas"}}]}, "noCubre": ["Condiciones preexistentes durante los primeros 12 meses", "Embarazo o parto, salvo complicaciones", "Trastornos mentales y abuso de sustancias", "Sin beneficio de bienestar ni chequeo preventivo", "Guard 4000: sin medicinas, laboratorio, rayos X ni estudios", "Hospitalización que empieza viernes o sábado, salvo emergencia o cirugía al día siguiente (no aplica en Florida)", "Tratamiento cosmético e infertilidad", "Gastos dentales, salvo lo que diga la póliza", "Exámenes o lentes, lentes de contacto y audífonos", "Tratamientos experimentales o de investigación", "Servicios fuera de EE. UU., salvo emergencias", "Lesiones por intoxicación, drogas, autolesión, guerra, delitos o deportes de riesgo/profesionales"]}, "base-uhc-health-protectorguard": {"coberturas": {"tipo": "indemnizacion", "filas": [{"concepto": "Hospital (por día)", "valores": {"HPG Select 2000": "Año 1: $2,000 por día; año 2: $4,000 por día, solo si es por accidente (días ilimitados)", "HPG Preferred 4000": "Año 1: $4,000 por día; año 2: $8,000 por día, solo si es por accidente (días ilimitados)", "HPG Premier 5000": "Año 1: $5,000 por día; año 2: $10,000 por día, solo si es por accidente (días ilimitados)"}}, {"concepto": "Primer día de hospital (admisión)", "valores": {"HPG Select 2000": "$2,000 por día (máx. 1 día)", "HPG Preferred 4000": "$3,000 por día (máx. 1 día)", "HPG Premier 5000": "$3,000 por día (máx. 1 día)"}}, {"concepto": "Cuidados intensivos (ICU/CCU)", "valores": {"HPG Select 2000": "$2,000 por día (máx. 60 días), además del hospital", "HPG Preferred 4000": "$4,000 por día (máx. 60 días), además del hospital", "HPG Premier 5000": "$5,000 por día (máx. 60 días), además del hospital"}}, {"concepto": "Sala de emergencia", "valores": {"HPG Select 2000": "$300 por día (máx. 3 días)", "HPG Preferred 4000": "$500 por día (máx. 3 días)", "HPG Premier 5000": "$1,000 por día (máx. 3 días)"}}, {"concepto": "Consultas en consultorio (máximo combinado)", "valores": {"HPG Select 2000": "Año 1: 4 visitas; año 2: 6 visitas", "HPG Preferred 4000": "Año 1: 6 visitas; año 2: 8 visitas", "HPG Premier 5000": "Año 1: 10 visitas; año 2: 12 visitas"}}, {"concepto": "Consulta médica", "valores": {"HPG Select 2000": "$80 por visita", "HPG Preferred 4000": "$100 por visita", "HPG Premier 5000": "$150 por visita"}}, {"concepto": "Especialista / Urgencias", "valores": {"HPG Select 2000": "$100 por visita", "HPG Preferred 4000": "$125 por visita", "HPG Premier 5000": "$150 por visita de especialista; $200 por visita de urgencias"}}, {"concepto": "Cirugía (7 niveles)", "valores": {"HPG Select 2000": "$250-$25,000 por día (días ilimitados)", "HPG Preferred 4000": "$375-$37,500 por día (días ilimitados)", "HPG Premier 5000": "$500-$50,000 por día (días ilimitados)"}}, {"concepto": "Cirugía ambulatoria (centro ambulatorio)", "valores": {"HPG Select 2000": "$1,000 por día (máx. 10 días)", "HPG Preferred 4000": "$2,000 por día (máx. 10 días)", "HPG Premier 5000": "$4,000 por día (máx. 10 días)"}}, {"concepto": "Laboratorio ambulatorio", "valores": {"HPG Select 2000": "$30 por prueba (máx. 10 pruebas combinadas)", "HPG Preferred 4000": "$50 por prueba (máx. 10 pruebas combinadas)", "HPG Premier 5000": "$50 por prueba (máx. 10 pruebas combinadas)"}}, {"concepto": "Rayos X y otros diagnósticos (ultrasonido, EKG...)", "valores": {"HPG Select 2000": "$50 por prueba", "HPG Preferred 4000": "$75 por prueba", "HPG Premier 5000": "$75 por prueba"}}, {"concepto": "MRI / PET / CAT", "valores": {"HPG Select 2000": "$400 por prueba", "HPG Preferred 4000": "$400 por prueba", "HPG Premier 5000": "$1,000 por prueba"}}, {"concepto": "Medicinas", "valores": {"HPG Select 2000": "Genérico $10 y marca $40 por receta; año 1: 12 recetas, año 2: 17 recetas", "HPG Preferred 4000": "Genérico $20 y marca $60 por receta; año 1: 12 recetas, año 2: 17 recetas", "HPG Premier 5000": "Genérico $20 y marca $60 por receta; año 1: 20 recetas, año 2: 25 recetas"}}, {"concepto": "Quimioterapia / radiación (no oral)", "valores": {"HPG Select 2000": "$1,000 por día (máx. 40 días)", "HPG Preferred 4000": "$2,000 por día (máx. 40 días)", "HPG Premier 5000": "$2,500 por día (máx. 60 días)"}}, {"concepto": "Examen físico (bienestar)", "valores": {"HPG Select 2000": "$100 por examen (máx. 1)", "HPG Preferred 4000": "$125 por examen (máx. 1)", "HPG Premier 5000": "$150 por examen (máx. 1)"}}, {"concepto": "Ambulancia", "valores": {"HPG Select 2000": "Terrestre/acuática $1,000 por viaje; aérea $5,000 por viaje (máx. 1 viaje de cualquier tipo)", "HPG Preferred 4000": "Terrestre/acuática $1,000 por viaje; aérea $5,000 por viaje (máx. 1 viaje de cualquier tipo)", "HPG Premier 5000": "Terrestre/acuática $1,000 por viaje; aérea $5,000 por viaje (máx. 1 viaje de cualquier tipo)"}}, {"concepto": "Máximo del plan", "valores": {"HPG Select 2000": "$2 millones por año calendario y $5 millones de por vida, por persona", "HPG Preferred 4000": "$2 millones por año calendario y $5 millones de por vida, por persona", "HPG Premier 5000": "$2 millones por año calendario y $5 millones de por vida, por persona"}}, {"concepto": "Telemedicina (HealthiestYou)", "valores": {"HPG Select 2000": "$0, visitas ilimitadas", "HPG Preferred 4000": "$0, visitas ilimitadas", "HPG Premier 5000": "$0, visitas ilimitadas"}}]}, "noCubre": ["Condiciones preexistentes durante los primeros 12 meses", "Embarazo o parto, salvo complicaciones", "Trastornos mentales y abuso de sustancias", "No es seguro médico ni cobertura mínima del ACA: paga montos fijos, sin máximo de bolsillo", "Hospitalización que empieza viernes o sábado, salvo emergencia o cirugía al día siguiente (no aplica en Florida)", "Tratamiento cosmético e infertilidad", "Gastos dentales, salvo lo que diga la póliza", "Exámenes o lentes, lentes de contacto y audífonos", "Tratamientos experimentales o de investigación", "Servicios fuera de EE. UU., salvo emergencias", "Internación para rehabilitación, cuidado de custodia o enfermería", "Lesiones por intoxicación, drogas, autolesión, guerra, delitos o deportes de riesgo/profesionales"]}, "base-uhc-short-term-medical": {"coberturas": {"tipo": "medico", "filas": [{"concepto": "Deducible", "valores": {"Short Term Value": "$2,500, $5,000, $7,500 o $15,000 por persona, por plazo (máx. 2 por familia)", "Short Term Plus Elite": "$2,500, $5,000, $7,500, $10,000 o $15,000 por persona, por plazo (máx. 2 por familia)", "Short Term Copay": "$2,500, $5,000, $7,500, $10,000 o $15,000 por persona, por plazo (máx. 2 por familia)"}}, {"concepto": "Coaseguro (lo que usted paga después del deducible)", "valores": {"Short Term Value": "30%", "Short Term Plus Elite": "0%", "Short Term Copay": "20%"}}, {"concepto": "Máximo de bolsillo (coaseguro, después del deducible)", "valores": {"Short Term Value": "$10,000 por persona, por plazo", "Short Term Plus Elite": "$0", "Short Term Copay": "$5,000 por persona, por plazo"}}, {"concepto": "Máximo del plan", "valores": {"Short Term Value": "$1 millón por persona, por plazo (Indiana: $2 millones)", "Short Term Plus Elite": "$2 millones por persona, por plazo", "Short Term Copay": "$2 millones por persona, por plazo"}}, {"concepto": "Consulta médica", "valores": {"Short Term Value": "30% después del deducible", "Short Term Plus Elite": "Sin costo después del deducible", "Short Term Copay": "$50 de copago (1 a 4 visitas según la duración del plan; después, deducible y coaseguro)"}}, {"concepto": "Urgencias (urgent care)", "valores": {"Short Term Value": "$50 de copago", "Short Term Plus Elite": "$50 de copago", "Short Term Copay": "$50 de copago"}}, {"concepto": "Sala de emergencia", "valores": {"Short Term Value": "30% después del deducible (+$500 de deducible adicional si no lo ingresan)", "Short Term Plus Elite": "Sin costo después del deducible (+$500 de deducible adicional si no lo ingresan)", "Short Term Copay": "20% después del deducible (+$500 de deducible adicional si no lo ingresan)"}}, {"concepto": "Hospital, cirugía ambulatoria, laboratorio y rayos X", "valores": {"Short Term Value": "30% después del deducible", "Short Term Plus Elite": "Sin costo después del deducible", "Short Term Copay": "20% después del deducible"}}, {"concepto": "Medicinas nivel 1", "valores": {"Short Term Value": "$25 de copago, sin deducible", "Short Term Plus Elite": "$25 de copago, sin deducible", "Short Term Copay": "$25 de copago, sin deducible"}}, {"concepto": "Medicinas niveles 2 a 4", "valores": {"Short Term Value": "30% después del deducible (máx. $2,500 de gasto cubierto por persona, por plazo)", "Short Term Plus Elite": "Sin costo después del deducible (máx. $5,000 de gasto cubierto por persona, por plazo)", "Short Term Copay": "20% después del deducible (máx. $5,000 de gasto cubierto por persona, por plazo)"}}, {"concepto": "Preventivo (mamografía, Papanicolaou, próstata)", "valores": {"Short Term Value": "30% después del deducible", "Short Term Plus Elite": "Sin costo después del deducible", "Short Term Copay": "20% después del deducible"}}, {"concepto": "Ambulancia aérea", "valores": {"Short Term Value": "Hasta $5,000 de gasto cubierto por persona, por plazo", "Short Term Plus Elite": "Hasta $5,000 de gasto cubierto por persona, por plazo", "Short Term Copay": "Hasta $5,000 de gasto cubierto por persona, por plazo"}}, {"concepto": "Columna y espalda (ambulatorio, sin cirugía)", "valores": {"Short Term Value": "Hasta $2,500 de gasto cubierto", "Short Term Plus Elite": "Hasta $2,500 de gasto cubierto", "Short Term Copay": "Hasta $2,500 de gasto cubierto"}}, {"concepto": "Rehabilitación / centro de cuidado extendido", "valores": {"Short Term Value": "Máx. 60 días combinados por persona, por plazo", "Short Term Plus Elite": "Máx. 60 días combinados por persona, por plazo", "Short Term Copay": "Máx. 60 días combinados por persona, por plazo"}}, {"concepto": "Trasplantes fuera de un Centro de Excelencia", "valores": {"Short Term Value": "1 trasplante por persona, máx. $100,000", "Short Term Plus Elite": "1 trasplante por persona, máx. $100,000", "Short Term Copay": "1 trasplante por persona, máx. $100,000"}}, {"concepto": "Red", "valores": {"Short Term Value": "Choice (UnitedHealthcare); fuera de la red solo emergencias", "Short Term Plus Elite": "Choice (UnitedHealthcare); fuera de la red solo emergencias", "Short Term Copay": "Choice (UnitedHealthcare); fuera de la red solo emergencias"}}, {"concepto": "Duración (Florida)", "valores": {"Short Term Value": "1 a 12 meses (menos un día)", "Short Term Plus Elite": "1 a 12 meses (menos un día)", "Short Term Copay": "1 a 12 meses (menos un día)"}}]}, "noCubre": ["Condiciones preexistentes: nunca se cubren (revisa los 24 meses anteriores, incluye embarazo ya existente)", "Embarazo, salvo complicaciones", "Atención fuera de la red que no sea emergencia", "Trastornos mentales y tratamiento de sustancias por orden judicial, salvo lo que diga el certificado", "Chequeos de rutina y vacunas de adultos (p. ej. la de la gripe)", "Cirugía de obesidad o control de peso", "Infertilidad, esterilización y aborto (salvo riesgo para la madre)", "Dental, salvo lesión accidental de dientes naturales", "Lentes, lentes de contacto, audífonos y cirugía refractiva", "Reemplazo de articulaciones, salvo por lesión cubierta", "Amígdalas, adenoides, oído medio, hemorroides o hernia si no es emergencia", "Hospitalización que empieza viernes o sábado, salvo emergencia o cirugía al día siguiente"]}, "base-uhc-triterm-medical": {"coberturas": {"tipo": "medico", "filas": [{"concepto": "Deducible", "valores": {"TriTerm Value": "$2,500, $5,000, $7,500, $10,000, $12,500 o $15,000 por persona, por plazo (máx. 2 por familia)", "TriTerm Copay Select Max": "$2,500, $5,000, $7,500, $10,000 o $12,500 por persona, por plazo (máx. 2 por familia)", "TriTerm Plan 100 Max": "$5,000, $7,500, $10,000 o $12,500 por persona, por plazo (máx. 2 por familia)"}}, {"concepto": "Coaseguro (lo que usted paga después del deducible)", "valores": {"TriTerm Value": "30%", "TriTerm Copay Select Max": "30%", "TriTerm Plan 100 Max": "0%"}}, {"concepto": "Máximo de bolsillo (coaseguro, después del deducible)", "valores": {"TriTerm Value": "$10,000 por persona, por plazo", "TriTerm Copay Select Max": "$4,500 por persona, por plazo", "TriTerm Plan 100 Max": "$0"}}, {"concepto": "Máximo del plan", "valores": {"TriTerm Value": "$2 millones por persona, de por vida", "TriTerm Copay Select Max": "$2 millones por persona, de por vida", "TriTerm Plan 100 Max": "$2 millones por persona, de por vida"}}, {"concepto": "Consulta médica", "valores": {"TriTerm Value": "30% de coaseguro después del deducible", "TriTerm Copay Select Max": "$50 de copago las primeras 4 visitas por persona, por plazo (después, deducible y coaseguro)", "TriTerm Plan 100 Max": "Sin costo después del deducible"}}, {"concepto": "Urgencias (urgent care)", "valores": {"TriTerm Value": "$75 de copago", "TriTerm Copay Select Max": "$75 de copago", "TriTerm Plan 100 Max": "Sin costo después del deducible"}}, {"concepto": "Sala de emergencia", "valores": {"TriTerm Value": "30% de coaseguro después del deducible (solo enfermedad: +$500 de deducible si no lo ingresan)", "TriTerm Copay Select Max": "30% de coaseguro después del deducible (solo enfermedad: +$500 de deducible si no lo ingresan)", "TriTerm Plan 100 Max": "Sin costo después del deducible (solo enfermedad: +$500 de deducible si no lo ingresan)"}}, {"concepto": "Hospital, cirugía ambulatoria, laboratorio y rayos X", "valores": {"TriTerm Value": "30% de coaseguro después del deducible", "TriTerm Copay Select Max": "30% de coaseguro después del deducible", "TriTerm Plan 100 Max": "Sin costo después del deducible"}}, {"concepto": "Preventivo", "valores": {"TriTerm Value": "No cubre", "TriTerm Copay Select Max": "$50 de copago (máx. $200 por persona, por plazo; 6 meses de espera solo en el plazo 1)", "TriTerm Plan 100 Max": "Sin costo después del deducible (máx. $200 por persona, por plazo; 6 meses de espera solo en el plazo 1)"}}, {"concepto": "Medicinas nivel 1", "valores": {"TriTerm Value": "No cubre (se da tarjeta de descuento)", "TriTerm Copay Select Max": "$25 de copago", "TriTerm Plan 100 Max": "Sin costo después del deducible (máx. $5,000 de gasto cubierto por persona, por plazo)"}}, {"concepto": "Medicinas niveles 2 a 4", "valores": {"TriTerm Value": "No cubre (se da tarjeta de descuento)", "TriTerm Copay Select Max": "Deducible de medicinas $500, luego: nivel 2 $55, nivel 3 $75, nivel 4 50% (máx. $5,000 de gasto cubierto por persona, por plazo)", "TriTerm Plan 100 Max": "Sin costo después del deducible (máx. $5,000 de gasto cubierto por persona, por plazo)"}}, {"concepto": "Red", "valores": {"TriTerm Value": "Choice Plus; fuera de la red: penalidad de 25% y deducible doble, sin máximo de bolsillo", "TriTerm Copay Select Max": "Choice; fuera de la red solo emergencias", "TriTerm Plan 100 Max": "Choice; fuera de la red solo emergencias"}}, {"concepto": "Columna y espalda (ambulatorio)", "valores": {"TriTerm Value": "Solo tratamiento hospitalario y quirúrgico", "TriTerm Copay Select Max": "Hasta $5,000 de gasto cubierto por persona, por plazo", "TriTerm Plan 100 Max": "Hasta $5,000 de gasto cubierto por persona, por plazo"}}, {"concepto": "Rehabilitación / centro de cuidado extendido", "valores": {"TriTerm Value": "Máx. 60 días combinados por persona, por plazo", "TriTerm Copay Select Max": "Máx. 60 días combinados por persona, por plazo", "TriTerm Plan 100 Max": "Máx. 60 días combinados por persona, por plazo"}}, {"concepto": "Hospicio", "valores": {"TriTerm Value": "Máx. 180 días de por vida", "TriTerm Copay Select Max": "Máx. 180 días de por vida", "TriTerm Plan 100 Max": "Máx. 180 días de por vida"}}, {"concepto": "Trasplantes fuera de un Centro de Excelencia", "valores": {"TriTerm Value": "1 trasplante en los 36 meses, máx. $100,000", "TriTerm Copay Select Max": "1 trasplante en los 36 meses, máx. $100,000", "TriTerm Plan 100 Max": "1 trasplante en los 36 meses, máx. $100,000"}}, {"concepto": "Duración", "valores": {"TriTerm Value": "3 plazos: 364 + 365 + 365 días", "TriTerm Copay Select Max": "3 plazos: 364 + 365 + 365 días", "TriTerm Plan 100 Max": "3 plazos: 364 + 365 + 365 días"}}, {"concepto": "Cargo de solicitud", "valores": {"TriTerm Value": "$40, no reembolsable", "TriTerm Copay Select Max": "$40, no reembolsable", "TriTerm Plan 100 Max": "$40, no reembolsable"}}]}, "noCubre": ["Condiciones preexistentes los primeros 12 meses (revisa los 24 meses anteriores; incluye embarazo ya existente)", "Amígdalas, adenoides, oído medio, hemorroides, hernia u órganos reproductivos los primeros 6 meses, salvo emergencia", "Embarazo, salvo complicaciones", "Trastornos mentales y tratamiento de sustancias por orden judicial", "Value: medicinas ambulatorias y tratamiento ambulatorio de columna y espalda", "Planes Max: atención fuera de la red que no sea emergencia", "Chequeos de rutina, salvo el beneficio preventivo indicado", "Cirugía de obesidad o control de peso", "Infertilidad, esterilización y aborto (salvo riesgo para la madre)", "Dental, salvo lesión accidental de dientes naturales", "Lentes, lentes de contacto, audífonos y cirugía refractiva", "Reemplazo de articulaciones, salvo por lesión cubierta"]}});
const conPdfs = (p) => {
  const x = PDF_UHC[p.id];
  if (!x) return p;
  p = { ...p, ...(coberturasUhcDemo()[p.id] || {}) };
  const fila = (n) => ({ nombre: n.replace(/\.pdf$/i, ''), archivo: `unitedhealthcare/${n}` });
  return { ...p, brochures: x.brochures.map(fila), materialAgente: x.materialAgente.map(fila) };
};
const brochuresDeLaDemo = new Map(); // blob -> File, solo en memoria
const pdfsDelFormulario = (init) => {
  const fd = init && init.body;
  if (!fd || typeof fd.getAll !== 'function') return [];
  return fd.getAll('files').filter((f) => f && /pdf/i.test(f.type || f.name || ''));
};
const guardarPdfDemo = (f) => {
  const blob = `cotizador/demo/${newId('pdf')}-${String(f.name || 'brochure.pdf').replace(/[^A-Za-z0-9._-]+/g, '-')}`;
  brochuresDeLaDemo.set(blob, f);
  return { nombre: String(f.name || 'Brochure').replace(/\.pdf$/i, ''), blob };
};
const claveDeArchivo = (s) => String(s || '').split(/[/\\]/).pop().trim().toLowerCase();

const semillaDelCatalogo = () => [
  ...productosBaseUhcDemo().map(conPdfs),
  {
    id: 'cp_demo_1', nombre: 'Accidente Esencial', compania: 'Aseguradora Ejemplo Sol', categoria: 'accidente', activo: true,
    estadosModo: 'solo', estados: ['FL'], edadMin: 19, edadMax: 64, underwriting: 'gi',
    preexistencias: 'No cubre enfermedad: solo accidentes.', esperas: 'Chequeo preventivo a los 90 días.',
    beneficios: 'Emergencias y ambulancia por accidente\nHospitalización e ICU por accidente\nFracturas, dislocaciones y terapia\nMuerte accidental',
    comision: '40% primer año · 7% renovación', notasInternas: 'Efectividad el 1 o el 15 del mes.', cuotaInscripcion: '',
    tarifa: { modo: 'cobertura', porEdad: false, planes: ['Plan 1', 'Plan 2'], filas: [{ desde: 0, hasta: 120, precios: {
      'Plan 1': { personal: 15, pareja: 19, hijos: 22, familia: 27 },
      'Plan 2': { personal: 19, pareja: 24, hijos: 28, familia: 34 } } }] },
    requisitos: ['Nombre y apellido', 'Dirección', 'Fecha de nacimiento', 'Número de Seguro Social o ITIN', 'Ocupación', 'Beneficiario (nombre, fecha de nacimiento y parentesco)', 'Datos bancarios para el pago'],
    brochures: [{ nombre: 'Resumen de cobertura (ejemplo)', url: '' }],
  },
  {
    id: 'cp_demo_2', nombre: 'Hospital Diario', compania: 'Aseguradora Ejemplo Sol', categoria: 'hospital', activo: true,
    estadosModo: 'solo', estados: ['FL'], edadMin: 19, edadMax: 64, underwriting: 'con',
    preexistencias: 'Preexistencias excluidas los primeros 12 meses.', esperas: 'Según póliza.',
    beneficios: 'Paga una cantidad fija por cada día en el hospital\nAdmisión, ICU y ambulancia',
    comision: '35% primer año', notasInternas: 'Pedir peso y estatura. La tarifa queda fija a la edad de compra.', cuotaInscripcion: '',
    tarifa: { modo: 'cobertura', porEdad: true, planes: ['Plan 1', 'Plan 2', 'Plan 3'], filas: coberturaPorEdad(['Plan 1', 'Plan 2', 'Plan 3'], 14, 7) },
    requisitos: ['Nombre como aparece en el Seguro Social', 'Número de Seguro Social', 'Foto del ID', 'Condiciones médicas preexistentes', 'Datos bancarios para el pago'],
    brochures: [{ nombre: 'Brochure en español (ejemplo)', url: '' }],
  },
  {
    id: 'cp_demo_3', nombre: 'Indemnización Fija 5000', compania: 'Compañía Nacional Ejemplo', categoria: 'indemnity', activo: true,
    estadosModo: 'excepto', estados: ['MA', 'NJ', 'NY', 'VT', 'WA'], edadMin: 18, edadMax: 64, underwriting: 'con',
    preexistencias: 'Revisión de 12 meses hacia atrás.', esperas: 'Bienestar a los 30 días.',
    beneficios: 'Cantidades fijas por hospitalización, cirugía y visitas\nTelemedicina incluida',
    comision: '20% primer año', notasInternas: '', cuotaInscripcion: '',
    tarifa: { modo: 'cobertura', porEdad: true, planes: ['Básico', 'Plus'], filas: coberturaPorEdad(['Básico', 'Plus'], 95, 30) },
    requisitos: ['Nombre y apellido', 'Dirección', 'Fecha de nacimiento', 'Foto del ID', 'Datos bancarios para el pago'],
    brochures: [],
  },
  {
    id: 'cp_demo_4', nombre: 'Corto Plazo Plus', compania: 'Compañía Nacional Ejemplo', categoria: 'short_term', activo: true,
    estadosModo: 'solo', estados: ['FL', 'GA', 'TX', 'TN'], edadMin: 18, edadMax: 64, underwriting: 'con',
    preexistencias: 'Sin beneficios por preexistencias los primeros 12 meses.', esperas: 'Preventivo a los 6 meses.',
    beneficios: 'Cobertura médica temporal más amplia que un plan de beneficio limitado',
    comision: '', notasInternas: 'Se cotiza en el portal del carrier.', cuotaInscripcion: '',
    tarifa: { modo: 'manual', porEdad: false, planes: [], filas: [] },
    requisitos: ['Nombre y apellido', 'Fecha de nacimiento', 'Preguntas de salud del carrier'], brochures: [],
  },
  {
    id: 'cp_demo_5', nombre: 'Membresía Médica', compania: 'Red Médica Ejemplo', categoria: 'acceso', activo: true,
    estadosModo: 'todos', estados: [], edadMin: '', edadMax: '', underwriting: 'no_aplica',
    preexistencias: 'Sin restricciones por preexistencias.', esperas: 'Activa desde el primer día.',
    beneficios: 'Consultas de cuidado primario y telemedicina\nDescuentos en medicinas y laboratorios\nAyuda para negociar facturas de hospital\nNo es un seguro médico',
    comision: '$15 única + $3 mensual', notasInternas: '', cuotaInscripcion: 40,
    tarifa: { modo: 'cobertura', porEdad: false, planes: ['Membresía'], filas: [{ desde: 0, hasta: 120, precios: {
      'Membresía': { personal: 59, pareja: 79, hijos: 79, familia: 99 } } }] },
    requisitos: ['Nombre y apellido', 'Dirección', 'Correo electrónico', 'Fecha de nacimiento', 'Datos bancarios para el pago'],
    brochures: [{ nombre: 'Comparación de planes (ejemplo)', url: '' }],
  },
  {
    id: 'cp_demo_6', nombre: 'Dental Sonrisa', compania: 'Dental Ejemplo', categoria: 'dental', activo: true,
    estadosModo: 'solo', estados: ['FL'], edadMin: 0, edadMax: 99, underwriting: 'gi',
    preexistencias: '', esperas: '6 meses para servicios básicos y mayores (se exonera con cobertura previa).',
    beneficios: 'Limpiezas y chequeos\nDeducible $50, máximo anual $1,000\nNo pide Seguro Social',
    comision: '20%', notasInternas: 'Precio por persona: se suma cada miembro.', cuotaInscripcion: '',
    tarifa: { modo: 'por_persona', porEdad: true, planes: ['Copago', 'Porcentaje'], filas: [
      { desde: 0, hasta: 18, precios: { Copago: 26, Porcentaje: 39 } },
      { desde: 19, hasta: 39, precios: { Copago: 24, Porcentaje: 31 } },
      { desde: 40, hasta: 63, precios: { Copago: 27, Porcentaje: 36 } },
      { desde: 64, hasta: 99, precios: { Copago: 35, Porcentaje: 47 } },
    ] },
    requisitos: ['Nombre y apellido de cada miembro', 'Fecha de nacimiento de cada miembro', 'Dirección', 'Datos bancarios para el pago'],
    brochures: [{ nombre: 'Resumen dental (ejemplo)', url: '' }],
  },
  {
    id: 'cp_demo_7', nombre: 'Dental Nacional', compania: 'Dental Ejemplo', categoria: 'dental', activo: true,
    estadosModo: 'excepto', estados: ['NY'], edadMin: '', edadMax: '', underwriting: 'gi',
    preexistencias: '', esperas: 'Preventivo desde el primer día.',
    beneficios: 'Red nacional de dentistas\nPreventivo cubierto al 100%',
    comision: '15%', notasInternas: '', cuotaInscripcion: '',
    tarifa: { modo: 'por_persona', porEdad: false, planes: ['Básico', 'Completo'], filas: [{ desde: 0, hasta: 120, precios: { 'Básico': 22, Completo: 38 } }] },
    requisitos: ['Nombre y apellido de cada miembro', 'Fecha de nacimiento de cada miembro', 'Datos bancarios para el pago'], brochures: [],
  },
  {
    id: 'cp_demo_9', nombre: 'Visión Clara', compania: 'Visión Ejemplo', categoria: 'vision', activo: true,
    estadosModo: 'todos', estados: [], edadMin: '', edadMax: '', underwriting: 'gi',
    preexistencias: '', esperas: 'Sin espera.',
    beneficios: 'Examen de la vista una vez al año\nLentes o lentes de contacto cada 12 meses',
    comision: '15%', notasInternas: '', cuotaInscripcion: '',
    tarifa: { modo: 'cobertura', porEdad: false, planes: ['Visión'], filas: [{ desde: 0, hasta: 120, precios: {
      'Visión': { personal: 9, pareja: 17, hijos: 18, familia: 26 } } }] },
    requisitos: ['Nombre y apellido de cada miembro', 'Fecha de nacimiento de cada miembro', 'Datos bancarios para el pago'], brochures: [],
  },
  {
    id: 'cp_demo_8', nombre: 'Plan ACA sin subsidio', compania: 'Mercado de Salud', categoria: 'aca', activo: true,
    estadosModo: 'todos', estados: [], edadMin: '', edadMax: '', underwriting: 'no_aplica',
    preexistencias: 'Cubre preexistencias.', esperas: 'Según inscripción abierta o evento de vida.',
    beneficios: 'Cubre condiciones preexistentes\nMáximo anual de gastos de bolsillo\nHospital, especialistas y medicamentos',
    comision: '', notasInternas: 'El precio sale del cotizador del Mercado.', cuotaInscripcion: '',
    tarifa: { modo: 'manual', porEdad: false, planes: [], filas: [] },
    requisitos: ['Datos de todos los miembros del hogar', 'Ingresos estimados', 'Documentos de estatus migratorio'], brochures: [],
  },
];
let cotizadorProductos = null;
const casosDeLaDemo = [];
let preferenciasDeLaDemo = {};
// Números de una agencia de ejemplo (inventados). Los clientes c1, c3 y c6 son
// los de SEED_CLIENTS, para que la alerta enseñe nombres de la demo.
function panelDeLaDemo() {
  const propias = casosDeLaDemo.length;
  const cerradas = casosDeLaDemo.filter((c) => c.estado === 'cerrado').length;
  const conVenta = casosDeLaDemo.filter((c) => c?.datos?.cierre?.resultado === 'venta').length;
  return {
    asesorias: { total: 48 + propias, enCurso: 9 + propias - cerradas, conVenta: 27 + conVenta, sinVenta: 12 + cerradas - conVenta, cerradasSinDato: 0 },
    salud: { verde: 21, amarillo: 17, rojo: 8, sinDato: 2 },
    capacidad: { alta: 4, media_alta: 7, media: 9, baja: 18, muy_baja: 8, sinDato: 2 },
    ilegibles: 0,
    embudo: { sometidas: 41, pendientes: 9, activas: 28, enGracia: 1, efectivas: 26, canceladas: 3, primaMensualActiva: 3874.5, noVisibles: 0 },
    alerta: {
      total: 3,
      polizas: [
        { id: 'demo-a1', clientId: 'c1', compania: 'UnitedHealthcare (Golden Rule)', plan: 'HPG Select 2000', dias: 44 },
        { id: 'demo-a2', clientId: 'c3', compania: 'Ameritas', plan: 'PrimeStar Care Boost', dias: 38 },
        { id: 'demo-a3', clientId: 'c6', compania: 'UnitedHealthcare (Golden Rule)', plan: 'Guard 5000', dias: 33 },
      ],
    },
    porCompania: [
      { compania: 'UnitedHealthcare (Golden Rule)', sometidas: 16, activas: 11 },
      { compania: 'Ameritas', sometidas: 11, activas: 8 },
      { compania: 'Manhattan Life', sometidas: 8, activas: 6 },
      { compania: 'Assurity', sometidas: 6, activas: 4 },
    ],
    companiasOcultas: 0,
    porTipo: [{ tipo: 'suppl:Hospitalización / Indemnización', n: 15 }, { tipo: 'dental', n: 12 }, { tipo: 'shortterm', n: 8 }, { tipo: 'suppl:Accidente', n: 6 }],
    renovacion: { tocaRenovar: 12, renovadas: 10 },
    agentes: [
      { agenteId: 'demo-1', nombre: 'Demo Agent', asesorias: 22 + propias, conVenta: 13 + conVenta, sometidas: 20, efectivas: 13 },
      { agenteId: 'demo-2', nombre: 'María López', asesorias: 15, conVenta: 8, sometidas: 12, efectivas: 8 },
      { agenteId: 'demo-3', nombre: 'Carlos Pérez', asesorias: 11, conVenta: 6, sometidas: 9, efectivas: 5 },
    ],
    truncado: false,
    limite: 500,
  };
}
// Cierre de la asesoría (Fase 4): el consentimiento que se crea aquí. Firmas de
// la demo sigue con su semilla.
const firmasDeLaDemo = [];
const catalogoDemo = () => {
  if (!cotizadorProductos) cotizadorProductos = semillaDelCatalogo().map((p) => ({ origen: 'agencia', ...p }));
  return cotizadorProductos;
};
// ── Encuestas (Lissette 2026-10-15) ─────────────────────────────────────────
// Una encuesta de ejemplo, sin respuestas: en la demo no hay clientes reales
// que contesten. Se puede crear, editar y borrar; al recargar vuelve a estar.
const encuestasDemo = [
  {
    id: 'enc-demo-1', nombre: enIdioma(PLANTILLAS_DE_ENCUESTA[0].nombre, 'es'),
    contenido: contenidoDePlantilla(PLANTILLAS_DE_ENCUESTA[0]), activa: true,
    createdAt: new Date().toISOString(), enviadas: 0, respondidas: 0,
  },
];
const encuestasRoute = (rest, method, body) => {
  if (rest.startsWith('/cliente/')) return ok({ data: [] });
  const [, id, action] = rest.split('/');
  if (id && !action && method === 'GET') {
    const e = encuestasDemo.find((x) => x.id === id);
    if (!e) return notFound();
    return ok({ data: { ...e, abiertas: 0, resumen: [], respuestas: [] } });
  }
  if (action === 'enlace') return ok({ data: { url: 'https://deskte.com/demo/', idioma: 'es' } });
  return collection(encuestasDemo, 'enc', (row) => {
    if (row.activa === undefined) row.activa = true;
    row.enviadas = row.enviadas || 0;
    row.respondidas = row.respondidas || 0;
  })(rest, method, body);
};

const cotizadorProductosRoute = (rest, method, body) =>
  collection(catalogoDemo(), 'cp', (row) => { row.origen = row.origen || 'agencia'; })(rest, method, body);

/**
 * Answer a demo request, or return null to let the caller reject as usual.
 * @param {string} path  request path, e.g. '/api/v1/quotes/q1/send'
 * @param {object} init  the fetch init (method + body)
 * @returns {Response|null}
 */

export function demoRespond(path, init = {}) {
  const method = (init.method || 'GET').toUpperCase();
  const clean = String(path || '').split('?')[0].replace(/\/+$/, '');
  const body = parseBody(init);

  const match = (base) => (clean === base ? '' : (clean.startsWith(base + '/') ? clean.slice(base.length) : null));

  let rest;

  // Traducir el mensaje del enlace de renovacion (Lissette 2026-09-09).
  //
  // En el CRM esto va a Azure OpenAI. En la demo no hay servidor, y sin esto
  // el visitante que apretara el boton se llevaria un aviso rojo en una pagina
  // de ventas. Se contestan SOLO los cuatro textos de fabrica —que son los que
  // el editor trae puestos al abrirse, o sea lo que de verdad va a apretar
  // alguien que pasa por aqui— con su version escrita por nosotros. Un texto
  // que el visitante haya reescrito no se puede traducir sin modelo: eso cae
  // al rechazo de siempre y el editor lo dice.
  if (clean === '/api/v1/ai/translate' && method === 'POST') {
    const de = String(body?.text || '').trim();
    const par = TRADUCCIONES_DE_LA_DEMO[de];
    if (!par) return null;
    return ok({ translated: par });
  }

  // Vista previa del correo (Mi Perfil → "Ver como la recibe el cliente").
  // En la app la arma el servidor; en la demo no hay, y sin esto el visitante
  // vería un aviso rojo. Se devuelve un correo de muestra fijo con la forma
  // del logo que haya tocado, para que el botón haga algo creíble.
  if (clean === '/api/v1/emails/vista-previa' && method === 'POST') {
    const en = body?.lang === 'en';
    const forma = body?.logoShape;
    const estilo = forma === 'redondo'
      ? 'width:44px;height:44px;border-radius:50%;background:#0a558c'
      : forma === 'marco'
        ? 'width:44px;height:28px;padding:4px;border:1px solid #d0d7de;border-radius:8px;background:#0a558c;background-clip:content-box'
        : 'width:44px;height:28px;background:#0a558c';
    const html = `<p style="margin:0 0 12px">${en ? 'Hi Maria,' : 'Hola María,'}</p>`
      + `<p style="margin:0 0 12px">${en ? 'Thank you for trusting us.' : 'Gracias por confiar en nosotros.'}</p>`
      + `<div style="margin-top:22px;padding-top:12px;border-top:1px solid #e2e8f0"><table cellpadding="0" cellspacing="0" border="0"><tr>`
      + `<td style="vertical-align:top;padding-right:12px"><div style="${estilo}"></div></td>`
      + `<td style="vertical-align:top"><div style="font-size:14px;font-weight:700;color:#1a1a1a">Demo Agent</div>`
      + `<div style="font-size:12px;color:#0077b6">Licensed Insurance Agent</div>`
      + `<div style="font-size:12px;color:#555;margin-top:4px">&#128231; agent@demo.deskte.com &nbsp;|&nbsp; &#128241; (305) 555-0142</div></td>`
      + `</tr></table></div>`;
    return ok({ data: { html, sinRemitente: false } });
  }

  // Quotes — plus the two actions the list row offers.
  if ((rest = match('/api/v1/quotes')) !== null) {
    const [, id, action] = rest.split('/');
    if (action === 'send' && method === 'POST') {
      const q = quotes.find((x) => x.id === id);
      if (!q) return notFound();
      if (q.status === 'DRAFT') q.status = 'SENT';
      // emailSent:false is the truth here — the demo has no mail transport, and
      // the UI words its toast off this flag instead of claiming a send.
      return ok({ ok: true, emailSent: false, landingUrl: `https://deskte.com/quote/demo-${id}`, publicToken: `demo-${id}` });
    }
    if (action === 'convert-to-policy' && method === 'POST') {
      const q = quotes.find((x) => x.id === id);
      if (!q) return notFound();
      // La misma regla que el servidor: manda la opcion que se eligio, si no la
      // que acepto el cliente, y si no la primera. Sin esto la demo no podia
      // ensenar el boton de "el cliente eligio esta" haciendo algo distinto
      // segun donde se pulse.
      const elegida = (q.items || []).find((it) => it.id === body?.itemId)
        || (q.items || []).find((it) => it.id === q.acceptedItemId)
        || (q.items || [])[0];
      q.status = 'CONVERTED';
      q.acceptedItemId = elegida ? elegida.id : q.acceptedItemId;
      q.acceptedAt = q.acceptedAt || new Date().toISOString();
      q.convertedPolicyId = newId('p');
      return ok({ data: { id: q.convertedPolicyId, quoteId: q.id } });
    }
    if (action === 'convert-to-invoice' && method === 'POST') {
      const q = quotes.find((x) => x.id === id);
      if (!q) return notFound();
      const existing = invoices.find((inv) => inv.quoteId === q.id);
      if (existing) return ok({ data: existing, alreadyExisted: true });
      // Price the invoice off the accepted option when there is one, else the
      // first — same rule the real conversion uses.
      const item = (q.items || [])[0] || {};
      const draft = {
        id: newId('inv'), number: `INV-26${String(90 + idSeq).slice(-2)}`, quoteId: q.id,
        clientId: q.clientId, recipientName: q.recipientName, recipientEmail: q.recipientEmail,
        issueDate: dateOnly(0), dueDate: dateOnly(14), currency: 'USD', title: q.title,
        items: [{ id: newId('ii'), description: item.description || q.title, qty: 1, rate: Number(item.unitPrice || 0) }],
        discount: 0, discountType: 'amount', taxRate: 0, terms: 'Net 14', notes: q.notes || '',
        payments: [], sentAt: null, viewedAt: null, createdAt: new Date().toISOString(),
      };
      invoices.unshift(draft);
      return ok({ data: draft, alreadyExisted: false });
    }
    return quotesRoute(rest, method, body);
  }

  if ((rest = match('/api/v1/invoices')) !== null) return invoicesRoute(rest, method, body);
  // El consentimiento de la asesoría. GET solo contesta por las que se crearon
  // aquí: una lista vacía dejaría en blanco la pantalla de Firmas de la demo.
  if ((rest = match('/api/v1/signatures')) !== null) {
    if (method === 'POST' && !rest) {
      // Sin enlace: en la demo no hay página de firma (abrir deskte.com/demo/ confundía).
      const row = { id: newId('sig'), status: 'PENDING', createdAt: new Date().toISOString(), portalUrl: '', ...body, emailSent: body?.channel === 'email' };
      firmasDeLaDemo.unshift(row);
      return ok({ data: row });
    }
    const clientId = new URLSearchParams(String(path || '').split('?')[1] || '').get('clientId');
    if (method === 'GET' && !rest && clientId && firmasDeLaDemo.some((f) => f.clientId === clientId)) {
      return ok({ data: firmasDeLaDemo.filter((f) => f.clientId === clientId) });
    }
    return null;
  }
  // Las pólizas que crea el cierre: la ficha las añade con el aviso
  // `deskte:poliza-creada` (las demás altas de la demo no llegan aquí: van por
  // su propio estado local, ver window.__deskteAuth).
  if ((rest = match('/api/v1/policies')) !== null && method === 'POST' && !rest) {
    return ok({ data: { id: newId('pol'), createdAt: new Date().toISOString(), ...body } });
  }
  // Entrevistas del cotizador guiado: en memoria, como todo lo de la demo.
  if ((rest = match('/api/v1/cotizador/casos')) !== null) {
    return collection(casosDeLaDemo, 'caso', (row) => {
      row.estado = row.estado || 'en_curso';
      row.updatedAt = new Date().toISOString();
      row.agenteNombre = row.agenteNombre || 'Demo Agent';
    })(rest, method, body);
  }
  // El panel (Fase 5). En producción lo cuenta el servidor; aquí, una agencia
  // INVENTADA de ejemplo más las asesorías que el visitante haga en la demo.
  if (clean === '/api/v1/cotizador/panel' && method === 'GET') return ok({ data: panelDeLaDemo() });
  if (clean === '/api/v1/cotizador/preferencias') {
    if (method === 'GET') return ok({ data: preferenciasDeLaDemo, puedeCambiar: true });
    if (method === 'PUT') { preferenciasDeLaDemo = body?.preferencias && typeof body.preferencias === 'object' ? body.preferencias : {}; return ok({ data: preferenciasDeLaDemo, puedeCambiar: true }); }
    return null;
  }
  if (clean === '/api/v1/cotizador/brochures' && method === 'POST') {
    const pdfs = pdfsDelFormulario(init);
    if (!pdfs.length) return new Response(JSON.stringify({ error: 'Solo se aceptan PDF' }), { status: 400, headers: { 'Content-Type': 'application/json' } });
    return ok({ data: pdfs.map(guardarPdfDemo) });
  }
  if (clean.startsWith('/api/v1/cotizador/brochures/') && method === 'GET') {
    const blob = decodeURIComponent(clean.slice('/api/v1/cotizador/brochures/'.length));
    const f = brochuresDeLaDemo.get(blob);
    return f ? new Response(f, { status: 200, headers: { 'Content-Type': 'application/pdf' } }) : notFound();
  }
  if (clean === '/api/v1/cotizador/base/brochures' && method === 'POST') {
    let subidos = 0;
    const tocados = new Set();
    const sinProducto = [];
    for (const f of pdfsDelFormulario(init)) {
      const clave = claveDeArchivo(f.name);
      const usan = catalogoDemo().filter((p) => p.origen === 'deskte' && ['brochures', 'materialAgente'].some((c) => (p[c] || []).some((b) => claveDeArchivo(b.archivo) === clave)));
      if (!usan.length) { sinProducto.push(f.name); continue; }
      const { blob } = guardarPdfDemo(f);
      subidos++;
      for (const p of usan) {
        for (const c of ['brochures', 'materialAgente']) for (const b of p[c] || []) if (claveDeArchivo(b.archivo) === clave) b.blob = blob;
        tocados.add(p.id);
      }
    }
    return ok({ data: { subidos, productosActualizados: tocados.size, sinProducto } });
  }
  if ((rest = match('/api/v1/cotizador/productos')) !== null) {
    const [, id, action] = rest.split('/');
    // Ocultar / mostrar: la agencia dice "este no lo vendo" sin borrarlo. En
    // la app real se guarda por agencia; un producto base no se borra nunca.
    if (action === 'ocultar' && method === 'POST') {
      const p = catalogoDemo().find((x) => x.id === id);
      if (!p) return notFound();
      p.oculto = !!body?.oculto;
      return ok({ data: p });
    }
    return cotizadorProductosRoute(rest, method, body);
  }

  if ((rest = match('/api/v1/reports/dashboards')) !== null) return dashboardsRoute(rest, method, body);

  if ((rest = match('/api/v1/doc-templates')) !== null) return docTemplatesRoute(rest, method, body);

  if ((rest = match('/api/v1/chat')) !== null) {
    const [, seg] = rest.split('/');
    if (seg === 'channels' && method === 'GET') {
      return ok({
        data: canalesDeChat.map((c) => {
          const msgs = mensajesDeChat[c.key] || [];
          return { ...c, unread: 0, lastMessageAt: msgs.length ? msgs[msgs.length - 1].createdAt : null };
        }),
      });
    }
    if (seg === 'messages') {
      const canal = new URLSearchParams(String(path).split('?')[1] || '').get('channel') || 'general';
      if (method === 'GET') return ok({ data: mensajesDeChat[canal] || [] });
      if (method === 'POST') {
        // Escribir SI funciona: se queda en memoria y se pierde al recargar,
        // como el resto de la demo.
        const fila = {
          ...delBot(newId('cm'), canal, String(body?.body || ''), null, 0),
          authorId: 'u1', author: { id: 'u1', displayName: 'Sofia Marrero' }, isSystem: false,
          createdAt: new Date().toISOString(),
        };
        (mensajesDeChat[canal] = mensajesDeChat[canal] || []).push(fila);
        return ok({ data: fila });
      }
    }
    if (seg === 'read' && method === 'POST') return ok({ ok: true });
    if (seg === 'read-states') return ok({ data: [] });
    if (seg === 'presence') return ok({ data: { online: [] } });
    if (seg === 'typing') return ok({ ok: true });
    if (seg === 'push-key') return ok({ enabled: false, key: '' });
    if (seg === 'event-rules') {
      if (method === 'GET') return ok({ data: reglasDeChat });
      if (method === 'PUT') {
        const entrantes = Array.isArray(body?.rules) ? body.rules : [];
        for (const r of entrantes) {
          if (!r || !r.event) continue;
          const fila = reglasDeChat.find((x) => x.event === r.event);
          if (fila) { fila.enabled = !!r.enabled; fila.channelKey = r.channelKey || 'general'; }
          else reglasDeChat.push({ event: r.event, enabled: !!r.enabled, channelKey: r.channelKey || 'general' });
        }
        return ok({ ok: true, data: reglasDeChat });
      }
    }
  }

  // Borradores de Correo Masivo. En memoria, como lo demas de aqui: guardar,
  // abrir y borrar funcionan y se pierden al recargar. Sin esto el boton
  // "Guardar borrador" de la demo contestaria con un error rojo.
  if ((rest = match('/api/v1/campaigns/drafts')) !== null) {
    const id = rest.replace(/^\//, '');
    const conCuenta = (d) => ({ ...d, selectedCount: Array.isArray(d.payload?.selectedIds) ? d.payload.selectedIds.length : 0 });
    if (!id && method === 'GET') {
      return ok({ data: [...campaignDrafts].sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt))).map(conCuenta) });
    }
    if (!id && method === 'POST') {
      const ahora = new Date().toISOString();
      const d = {
        id: newId('drf'), channel: body.channel || 'email', subject: body.subject || '', htmlBody: body.htmlBody || '',
        payload: body.payload || {}, createdAt: ahora, updatedAt: ahora, createdByName: 'Demo',
      };
      campaignDrafts.push(d);
      return ok({ ok: true, data: conCuenta(d) });
    }
    const d = campaignDrafts.find((x) => x.id === id);
    if (!d) return notFound();
    if (method === 'PUT') {
      Object.assign(d, {
        channel: body.channel || 'email', subject: body.subject || '', htmlBody: body.htmlBody || '',
        payload: body.payload || {}, updatedAt: new Date().toISOString(),
      });
      return ok({ ok: true, data: conCuenta(d) });
    }
    if (method === 'DELETE') {
      campaignDrafts.splice(campaignDrafts.indexOf(d), 1);
      return ok({ ok: true });
    }
    return notFound();
  }

  if ((rest = match('/api/v1/encuestas')) !== null) return encuestasRoute(rest, method, body);

  if ((rest = match('/api/v1/workflows')) !== null) {
    const [, id, action] = rest.split('/');
    // 'fire' (test) and 'run' (manual, against a client) both just bump the
    // counters so the card visibly reacts.
    if ((action === 'fire' || action === 'run') && method === 'POST') {
      const w = workflows.find((x) => x.id === id);
      if (!w) return notFound();
      w.runCount = (w.runCount || 0) + 1;
      w.lastRunAt = new Date().toISOString();
      return ok({ ok: true, fired: true, actions: (w.actions || []).length });
    }
    return workflowsRoute(rest, method, body);
  }

  if ((rest = match('/api/v1/sequences')) !== null) {
    const [, id, action] = rest.split('/');
    if (action === 'steps') {
      const s = sequences.find((x) => x.id === id);
      if (!s) return notFound();
      if (method === 'GET') return ok({ data: sequenceSteps[id] || [] });
      const steps = Array.isArray(body.steps) ? body.steps : [];
      sequenceSteps[id] = steps;
      s._count = { ...(s._count || {}), steps: steps.length };
      return ok({ data: steps });
    }
    if (action === 'enrollments') return ok({ data: [] });
    // EL DETALLE TIENE QUE TRAER LOS PASOS DENTRO. La pantalla los lee de
    // `seq.steps` (el servidor los incluye con un `include`), no del endpoint
    // `/steps`: sin esto el editor sale VACIO aunque la tarjeta diga "6
    // pasos", que es justo lo que pasaba hasta el 2026-09-21.
    if (id && !action && method === 'GET') {
      const s = sequences.find((x) => x.id === id);
      if (!s) return notFound();
      return ok({ data: { ...s, steps: sequenceSteps[id] || [] } });
    }
    return sequencesRoute(rest, method, body);
  }

  if ((rest = match('/api/v1/recurring-invoices')) !== null) {
    const [, id, action] = rest.split('/');
    if (action === 'run-now' && method === 'POST') {
      const r = recurringInvoices.find((x) => x.id === id);
      if (!r) return notFound();
      r.lastRunAt = new Date().toISOString();
      return ok({ ok: true, created: 1 });
    }
    return recurringRoute(rest, method, body);
  }

  // Calendar events feed both the Appointments ("Citas") module and the
  // calendar grid, so seeding it fills two views at once.
  if ((rest = match('/api/v1/calendar')) !== null) return calendarRoute(rest, method, body);
  if ((rest = match('/api/v1/appointments')) !== null) return citasRoute(rest, method, body);

  // Booking Link. Without these two, the module renders the raw fetch error as
  // its whole screen — the one place in the demo where a visitor met a stack
  // trace instead of a product.
  if (clean === '/api/v1/booking/editables' && method === 'GET') {
    const enlaces = bookingLinks.map((e) => ({ id: e.id, slug: e.slug, isActive: e.isActive, nombre: e.availability.nombre || '' }));
    return ok({ data: [{ id: 'u1', yo: true, enlaces, slug: enlaces[0].slug, isActive: enlaces[0].isActive }] });
  }
  if (clean === '/api/v1/booking/config') {
    const pedido = new URLSearchParams(String(path || '').split('?')[1] || '').get('enlace');
    const enlace = bookingLinks.find((e) => e.id === pedido) || bookingLinks[0];
    if (method === 'GET') return ok({ data: conTextos(enlace) });
    if (method === 'PUT' || method === 'PATCH') return ok({ data: conTextos(guardarEnlaceDemo(enlace, body)) });
    if (method === 'POST') {
      const n = bookingLinks.length + 1;
      const nuevo = { ...bookingLinks[0], id: `bc_demo_${n}`, slug: `sofia-marrero-${n}`, isActive: false,
        availability: { ...SEMANA_DEMO, nombre: (body && body.nombre) || '' } };
      bookingLinks.push(nuevo);
      return ok({ data: conTextos(nuevo) });
    }
  }
  if (clean === '/api/v1/booking/bookings' && method === 'GET') {
    return ok({ data: { bookings: bookingReservations } } );
  }

  // Solicitudes. Solo lo que la pantalla usa: la lista y el reenvio del
  // enlace. Reenviar vuelve a sellar `sentAt`, igual que el servidor de
  // verdad, asi que la columna del enlace pasa de "Vencido" a "Vence en 3 d"
  // delante de quien lo pulse.
  if ((rest = match('/api/v1/applications')) !== null) {
    const [, id, action] = rest.split('/');
    if (action === 'resend' && method === 'POST') {
      const a = applications.find((x) => x.id === id);
      if (!a) return notFound();
      a.sentAt = at(0);
      a.status = 'SENT';
      a.viewedAt = null;
      a.tokenRevokedAt = null;
      // emailSent:false es la verdad: la demo no manda correos.
      return ok({ data: { application: a, url: `https://deskte.com/application/demo-${id}`, emailSent: false, expiresInHours: 72 } });
    }
    if (action === 'revoke' && method === 'POST') {
      const a = applications.find((x) => x.id === id);
      if (!a) return notFound();
      a.tokenRevokedAt = at(0);
      return ok({ data: { application: a } });
    }
    if (!id && method === 'GET') return ok({ data: applications });
    // Las plantillas se piden al abrir el modulo. Sin esta linea la pantalla
    // entera se corona con "backend disabled" y no se llega ni a la lista.
    if (id === 'templates' && method === 'GET') return ok({ data: applicationTemplates });
  }

  // Buscador de hospitales: una muestra de Florida (datos publicos reales, los
  // arma scripts/hospitales/armar-hospitales.py en demoHospitales.js). Otro
  // estado sale vacio, igual que un estado sin hospitales.
  if ((rest = match('/api/v1/hospitales')) !== null && method === 'PUT' && rest.endsWith('/nota')) {
    const id = decodeURIComponent(rest.slice(1, -'/nota'.length));
    const texto = String(body?.texto || '').trim();
    if (!texto) { delete notasDeHospitalesDemo[id]; return ok({ nota: null }); }
    notasDeHospitalesDemo[id] = { texto, autorNombre: 'Sofia Marrero', actualizada: new Date().toISOString() };
    return ok({ nota: notasDeHospitalesDemo[id] });
  }
  if ((rest = match('/api/v1/hospitales')) !== null && method === 'GET') {
    const qs = new URLSearchParams(String(path).split('?')[1] || '');
    const estado = (qs.get('estado') || '').toUpperCase();
    const delEstado = HOSPITALES_DEMO.hospitales.filter((h) => h.estado === estado);
    if (rest.startsWith('/condados')) {
      const cuenta = {};
      delEstado.forEach((h) => { cuenta[h.condado] = (cuenta[h.condado] || 0) + 1; });
      return ok({ condados: Object.keys(cuenta).sort().map((condado) => ({ condado, hospitales: cuenta[condado] })), generado: HOSPITALES_DEMO.generado });
    }
    const condado = normalizarCondado(qs.get('condado') || '');
    return ok({
      hospitales: delEstado.filter((h) => !condado || normalizarCondado(h.condado) === condado).map((h) => ({ ...h, nota: notasDeHospitalesDemo[h.id] || null })),
      generado: HOSPITALES_DEMO.generado,
      puedeAnotar: true,
    });
  }

  // Cotizador del Mercado. Tres rutas: si esta configurado, el condado del ZIP
  // y la cotizacion. Ver PLANES_DEL_MERCADO arriba.
  if ((rest = match('/api/v1/marketplace')) !== null) {
    const [, seg] = rest.split('/');
    if (seg === 'status') return ok({ configured: true });
    if (seg === 'counties') {
      // Cualquier ZIP devuelve el mismo condado: el visitante escribe el suyo y
      // el cotizador sigue adelante en vez de pararse en "ZIP no encontrado".
      return ok({ counties: [{ fips: '12086', name: 'Miami-Dade', state: 'FL', zipcode: '33101' }] });
    }
    if (seg === 'providers' && method === 'GET') {
      // Directorio inventado: el visitante busca y algo sale.
      const q = decodeURIComponent((String(path).split('q=')[1] || '').split('&')[0]).replace(/\+/g, ' ').trim().toLowerCase();
      const todos = MEDICOS_DEMO_DIRECTORIO;
      return ok({ providers: q.length < 2 ? [] : todos.filter((m) => `${m.nombre} ${m.especialidades.join(' ')}`.toLowerCase().includes(q)) });
    }
    if (seg === 'providers-covered' && method === 'POST') {
      const coverage = {};
      (body?.planIds || []).forEach((plan, i) => {
        coverage[plan] = {};
        (body?.npis || []).forEach((npi, j) => { coverage[plan][npi] = (i + j) % 3 === 1 ? 'fuera' : 'en_red'; });
      });
      return ok({ coverage });
    }
    if (seg === 'drugs-covered' && method === 'POST') {
      // Cobertura inventada pero estable: el primer plan lo cubre todo, el
      // segundo deja fuera el último medicamento y el resto alterna.
      const coverage = {};
      (body?.planIds || []).forEach((plan, i) => {
        coverage[plan] = {};
        (body?.rxcuis || []).forEach((rx, j, todos) => {
          coverage[plan][rx] = i === 0 ? 'cubre'
            : i === 1 && j === todos.length - 1 ? 'no_cubre'
              : (i + j) % 3 === 0 ? 'generico' : 'cubre';
        });
      });
      return ok({ coverage });
    }
    if (seg === 'quote' && method === 'POST') {
      // El subsidio se simula con una regla simple y VISIBLE: por debajo de
      // 60.000 al año hay credito (y los `netPremium` de arriba ya lo llevan);
      // por encima, se cotiza a precio completo. No pretende ser el calculo del
      // Mercado — pretende que se vea que el credito cambia el precio.
      const conCredito = body?.applySubsidy !== false && Number(body?.income || 0) < 60000;
      const plans = PLANES_DEL_MERCADO.map((p) => ({
        ...p,
        netPremium: conCredito ? p.netPremium : p.premium,
        premiumWithCredit: conCredito ? p.premiumWithCredit : null,
      }));
      return ok({
        year: new Date().getFullYear() + 1,
        eligibility: { aptc: conCredito ? 428 : 0, csr: conCredito ? 'CSR 87%' : null, isMedicaidChip: false },
        plans,
        total: plans.length,
      });
    }
  }

  // Recursos + enlaces del portal por cliente.
  if ((rest = match('/api/v1/resources')) !== null) {
    const [, seg, tok, accion] = rest.split('/');
    if (seg === 'tokens') {
      if (!tok && method === 'GET') return ok({ data: resourceTokens });
      if (!tok && method === 'POST') {
        // Igual que el servidor de verdad: el token lo acuña AQUI, nunca lo
        // manda la pantalla, y nace con fecha. Idempotente por cliente
        // mientras el suyo siga vivo.
        const incoming = Array.isArray(body?.tokens) ? body.tokens : [body];
        const out = [];
        for (const t of incoming) {
          if (!t?.clientId) continue;
          const vivo = resourceTokens.find(
            (x) => x.clientId === t.clientId && !x.revokedAt && (!x.expiresAt || new Date(x.expiresAt) > new Date(at(0))),
          );
          const row = vivo || {
            token: newId('rtok').replace(/[^A-Za-z0-9_-]/g, '') + newId('x').replace(/[^A-Za-z0-9_-]/g, ''),
            clientId: t.clientId,
            createdBy: t.createdBy || 'u1',
            createdAt: dateOnly(0),
            expiresAt: at(30),
            revokedAt: null,
            estado: null,
          };
          if (!vivo) resourceTokens.push(row);
          out.push(row);
        }
        return ok({ data: out });
      }
      if (tok && accion === 'revoke' && method === 'POST') {
        const row = resourceTokens.find((x) => x.token === tok);
        if (!row) return notFound();
        row.revokedAt = at(0);
        row.estado = 'revoked';
        return ok({ data: { token: row.token, revokedAt: row.revokedAt } });
      }
    }
    if (!seg && method === 'GET') return ok({ data: resources });
  }

  // El buscador de medicamentos: en la demo no hay servidor que pregunte a la
  // NLM, asi que contesta un catalogo pequeño con la misma forma.
  if (clean === '/api/v1/medicamentos/buscar' && method === 'GET') {
    const q = decodeURIComponent((String(path).split('q=')[1] || '').split('&')[0]).trim().toLowerCase();
    return ok({ data: q.length < 2 ? [] : CATALOGO_DEMO_MEDICAMENTOS.filter((m) => m.nombre.toLowerCase().includes(q)) });
  }

  // Los medicamentos de la ficha, como los medicos: de verdad y hasta recargar.
  // HealthSherpa: cuenta conectada e "Inscribir en HealthSherpa" (Lissette
  // 2026-10-28, guia del Centro de Ayuda). Los enlaces creados van a la portada
  // publica de HealthSherpa: no abren ninguna inscripcion de nadie.
  if (clean === '/api/v1/healthsherpa' && method === 'GET') {
    return ok({ ok: true, data: {
      connected: true, state: 'connected', accountEmail: 'agent@demo.deskte.com',
      termsAcceptedAt: at(-30), lastSuccessAt: at(0), lastErrorAt: null, lastErrorCode: null,
      isActive: true, createdAt: at(-30), updatedAt: at(0),
    } });
  }
  if (clean === '/api/v1/healthsherpa/enrollment-session') {
    if (method === 'GET') {
      const clientId = new URLSearchParams(String(path).split('?')[1] || '').get('clientId') || '';
      return ok({
        ok: true,
        data: hsSesiones.filter((x) => x.clientId === clientId),
        prerelleno: ['nombre', 'fecha de nacimiento', 'correo', 'teléfono', 'estado', 'código postal', 'dirección', 'tamaño del hogar', 'ingreso anual'],
        faltante: [{ campo: 'sexo', motivo: 'vacio' }, { campo: 'si fuma', motivo: 'vacio' }],
      });
    }
    if (method === 'POST') {
      const row = {
        id: newId('hs'), clientId: body?.clientId || '', planYear: body?.planYear, flow: body?.flujo || 'agent_assisted',
        shoppingUrl: 'https://www.healthsherpa.com/',
        clientApplyUrl: body?.flujo === 'self_service' ? 'https://www.healthsherpa.com/' : null,
        hsConfirmationId: null, createdAt: at(0),
      };
      hsSesiones.unshift(row);
      return ok({ ok: true, data: row });
    }
  }

  // Integracion WhatsApp (la extension). Sin esto la pantalla de Ajustes sale
  // con un aviso rojo en la demo. El token es de mentira y no abre nada: el
  // servidor de verdad nunca lo ha visto.
  if (clean === '/api/v1/integrations/whatsapp-token') {
    if (method === 'GET') return ok({ ok: true, data: waToken });
    if (method === 'POST') {
      const token = 'dkt_ext_DEMO' + Math.random().toString(16).slice(2).padEnd(12, '0') + '0000demo0000a1b2';
      waToken = {
        hasToken: true, lastFour: token.slice(-4), lastUsedAt: null, createdAt: at(0),
        expiresAt: at(90), expired: false,
      };
      return ok({ ok: true, data: { token, lastFour: waToken.lastFour } });
    }
    if (method === 'DELETE') { waToken = { ...SIN_TOKEN_WA }; return ok({ ok: true }); }
  }

  // Lo que la extension guardo en la ficha (pestaña de mensajes, canal
  // WhatsApp). Solo Pedro Alvarez (c11, "contacto preferido: WhatsApp") trae
  // conversacion; las demas fichas salen vacias, como en una cuenta nueva.
  if ((rest = match('/api/v1/clients')) !== null) {
    const [, clientId, seccion] = rest.split('/');
    if (seccion === 'pasted-messages') {
      const channel = new URLSearchParams(String(path).split('?')[1] || '').get('channel') || body?.channel;
      const key = `${clientId}|${channel}`;
      if (method === 'GET') {
        const messages = pastedMessages[key] || [];
        return ok({ ok: true, data: { channel, messages, messageCount: messages.length, lastPastedAt: messages.length ? at(0) : null } });
      }
      if (method === 'POST') {
        const nuevos = (Array.isArray(body?.messages) ? body.messages : []).map((m) => ({ ...m, _pasted: true }));
        pastedMessages[key] = [...(pastedMessages[key] || []), ...nuevos];
        return ok({ ok: true, data: { channel, messageCount: pastedMessages[key].length, lastPastedAt: at(0) } });
      }
    }
  }

  if ((rest = match('/api/v1/clients')) !== null) {
    const [, clientId, seccion, medId] = rest.split('/');
    if (seccion === 'medications') {
      if (!medId && method === 'GET') return ok({ ok: true, data: clientMedications.filter((m) => m.clientId === clientId) });
      if (!medId && method === 'POST') {
        const row = {
          ...body, id: newId('med'), clientId, persona: body?.persona || 'primary',
          creadoPor: 'Lissette', actualizadoPor: 'Lissette', createdAt: at(0), updatedAt: at(0),
        };
        clientMedications.push(row);
        return ok({ ok: true, data: row, recortados: [] });
      }
      if (medId && method === 'PUT') {
        const i = clientMedications.findIndex((m) => m.id === medId && m.clientId === clientId);
        if (i === -1) return notFound();
        clientMedications[i] = { ...clientMedications[i], ...body, id: medId, clientId, actualizadoPor: 'Lissette', updatedAt: at(0) };
        return ok({ ok: true, data: clientMedications[i], recortados: [] });
      }
      if (medId && method === 'DELETE') {
        const i = clientMedications.findIndex((m) => m.id === medId && m.clientId === clientId);
        if (i === -1) return notFound();
        clientMedications.splice(i, 1);
        return ok({ ok: true });
      }
    }
  }

  // Los medicos de la ficha. Crear, editar y quitar funcionan de verdad y se
  // deshacen al recargar, como el resto de la demo.
  if ((rest = match('/api/v1/clients')) !== null) {
    const [, clientId, seccion, doctorId] = rest.split('/');
    // "Mis proveedores": los médicos de todas las fichas, cada uno una vez (E-405).
    if (clientId && clientId.startsWith('doctors-directory') && method === 'GET') {
      const vistos = new Map();
      for (const d of [...clientDoctors].reverse()) {
        const clave = String(d.npi || '').replace(/\D/g, '').length === 10 ? d.npi : `${d.nombre}|${d.telefono || ''}`;
        if (!d.nombre || vistos.has(clave)) { if (vistos.has(clave)) vistos.get(clave).usos += 1; continue; }
        const { nombre, especialidad, npi, centro, direccion, ciudad, estado, zip, telefono, fax, red } = d;
        vistos.set(clave, { clave, usos: 1, nombre, especialidad, npi, centro, direccion, ciudad, estado, zip, telefono, fax, red });
      }
      return ok({ ok: true, data: [...vistos.values()] });
    }
    if (seccion === 'doctors') {
      if (!doctorId && method === 'GET') {
        return ok({ ok: true, data: clientDoctors.filter((d) => d.clientId === clientId) });
      }
      if (!doctorId && method === 'POST') {
        const row = {
          ...body, id: newId('doc'), clientId,
          persona: body?.persona || 'primary',
          // La firma la pone el servidor, nunca la pantalla: aqui igual.
          creadoPor: 'Lissette', actualizadoPor: 'Lissette',
          createdAt: at(0), updatedAt: at(0),
        };
        clientDoctors.push(row);
        return ok({ ok: true, data: row, recortados: [] });
      }
      if (doctorId && method === 'PUT') {
        const i = clientDoctors.findIndex((d) => d.id === doctorId && d.clientId === clientId);
        if (i === -1) return notFound();
        clientDoctors[i] = { ...clientDoctors[i], ...body, id: doctorId, clientId, actualizadoPor: 'Lissette', updatedAt: at(0) };
        return ok({ ok: true, data: clientDoctors[i], recortados: [] });
      }
      if (doctorId && method === 'DELETE') {
        const i = clientDoctors.findIndex((d) => d.id === doctorId && d.clientId === clientId);
        if (i === -1) return notFound();
        clientDoctors.splice(i, 1);
        return ok({ ok: true });
      }
    }
  }

  // La linea de SMS y la compuerta del envio masivo.
  //
  // Sin esto, Email Masivo enseña el selector de canal con el boton de SMS
  // apagado y sin decir por que: `getPhoneIntegrations` se rechaza, `phoneInfo`
  // se queda en null y la pantalla no sabe si falta la linea, falta el
  // registro o lo apago la plataforma. O sea, justo lo que se le enseña a un
  // agente para que compre.
  //
  // Se contesta con una linea de Quo conectada y su 10DLC declarado, que es el
  // estado normal de una agencia que ya lo tiene puesto.
  if (clean === '/api/v1/phone' && method === 'GET') {
    // `{ data: ... }`, como el servidor de verdad: `getPhoneIntegrations` lee
    // `.data`. Sin el sobre, la pantalla recibe `undefined` y el boton de SMS
    // sale gris — encontrado conduciendo la demo, no leyendo.
    return ok({ data: {
      integrations: [{
        provider: 'QUO', phoneNumber: '+1 (305) 555-0142', isActive: true,
        useForSms: true, useForVoice: false, lastVerifiedAt: null,
      }],
      voiceBaaProviders: [], voiceBaaAttestations: [], voiceBaaBlocked: [],
      canCallInApp: false, canAttestBaa: true,
      callableProviders: ['RINGCENTRAL', 'MIGHTYCALL', 'SIGNALWIRE'],
      smsA2pProviders: ['QUO'],
      smsA2pAttestations: [{ provider: 'QUO', byUserId: null, byName: 'Demo', at: '2026-09-01T12:00:00.000Z' }],
      smsA2pBlocked: [],
      a2pProviders: ['TWILIO', 'SIGNALWIRE', 'RINGCENTRAL', 'QUO'],
      canSendBulkSms: true, bulkSmsBlockedReason: null,
      ringsAs: null,
    } });
  }

  // La bandeja unificada. Sin esto sale vacia en la demo (ver el bloque de
  // arriba): junta dos tablas que no estan en el SEED_* del CRM.
  if ((rest = match('/api/v1/conversations')) !== null) {
    const [, id, accion] = rest.split('/');
    const qs = new URLSearchParams(String(path).split('?')[1] || '');

    if (!id && method === 'GET') {
      let out = conversaciones.filter((c) => c.status === (qs.get('status') || 'open'));
      const canal = qs.get('channel');
      if (canal && canal !== 'all') out = out.filter((c) => c.channel === canal);
      const quien = qs.get('assignee');
      if (quien === 'unassigned') out = out.filter((c) => !c.assigneeId);
      else if (quien === 'mine') out = out.filter((c) => c.assigneeId === 'u2');
      if (qs.get('unread')) out = out.filter((c) => c.unreadCount > 0);
      const texto = (qs.get('q') || '').trim().toLowerCase();
      if (texto) out = out.filter((c) => c.clientName.toLowerCase().includes(texto));
      out = out.slice().sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
      return ok({ ok: true, data: out, pagination: { page: 1, limit: 50, total: out.length, pages: 1 } });
    }

    const hilo = conversaciones.find((c) => c.id === id);
    if (!hilo) return notFound();

    if (accion === 'messages' && method === 'GET') {
      return ok({
        ok: true,
        data: {
          id: hilo.id, channel: hilo.channel, clientId: hilo.clientId,
          clientName: hilo.clientName, status: hilo.status, assigneeId: hilo.assigneeId,
          messages: mensajesDeLaDemo[hilo.id] || [], hasMore: false,
        },
      });
    }
    if (accion === 'read' && method === 'POST') {
      hilo.unreadCount = 0;
      return ok({ ok: true });
    }
    if (accion === 'assign' && method === 'POST') {
      hilo.assigneeId = body?.assigneeId || null;
      hilo.assigneeName = hilo.assigneeId ? 'Agent' : null;
      return ok({ ok: true });
    }
    if (accion === 'status' && method === 'POST') {
      hilo.status = body?.status || 'open';
      return ok({ ok: true });
    }
    if (accion === 'reply' && method === 'POST') {
      // Se contesta DE VERDAD dentro de la demo: el mensaje aparece en el hilo
      // y mueve la portada, igual que en el CRM. Se borra al recargar.
      const cuerpo = String(body?.body || '').trim();
      if (!cuerpo) return ok({ ok: false, reason: 'empty' });
      const lista = mensajesDeLaDemo[hilo.id] || (mensajesDeLaDemo[hilo.id] = []);
      lista.push({
        id: newId('msg'), direction: 'outbound',
        subject: hilo.channel === 'email' ? (body?.subject || 'Re:') : null,
        body: cuerpo, createdAt: new Date().toISOString(),
      });
      hilo.lastMessageAt = new Date().toISOString();
      hilo.lastDirection = 'outbound';
      hilo.preview = cuerpo.slice(0, 160);
      return ok({ ok: true });
    }
    return notFound();
  }

  // Mover una tarjeta del embudo, y su valor / fecha de cierre.
  if ((rest = match('/api/v1/leads')) !== null) {
    const [, id, accion] = rest.split('/');
    const tarjeta = tarjetasDelEmbudo.find((l) => l.id === id);
    if (tarjeta && accion === 'stage' && method === 'POST') {
      const col = columnasDelEmbudo.find((x) => x.id === body?.stageId);
      if (!col) return notFound();
      tarjeta.stageId = col.id;
      // Igual que el servidor: se escribe la columna Y el estado.
      tarjeta.status = col.mappedStatus;
      return ok({ ok: true, data: { id: tarjeta.id, stageId: tarjeta.stageId, status: tarjeta.status } });
    }
    if (tarjeta && accion === 'deal' && method === 'PATCH') {
      if (body?.value !== undefined) tarjeta.value = body.value === null ? null : Number(body.value);
      if (body?.expectedCloseAt !== undefined) tarjeta.expectedCloseAt = body.expectedCloseAt;
      return ok({ ok: true });
    }
    // Lo demas de /leads lo sirve el SEED_* del CRM, en React.
    return null;
  }

  // El embudo arrastrable. Sin esto sale vacio en la demo.
  if ((rest = match('/api/v1/pipelines')) !== null) {
    const [, uno, dos] = rest.split('/');
    if (uno === 'board' && method === 'GET') return ok({ ok: true, data: tableroDeLaDemo() });
    if (!uno && method === 'GET') {
      return ok({ ok: true, data: [{ id: 'emb1', name: 'Embudo de ventas', isDefault: true, position: 0, stages: columnasDelEmbudo }] });
    }
    // Renombrar y quitar columnas funciona de verdad dentro de la demo.
    if (uno === 'stages' && dos) {
      const col = columnasDelEmbudo.find((x) => x.id === dos);
      if (!col) return notFound();
      if (method === 'PATCH') {
        if (typeof body?.name === 'string' && body.name.trim()) col.name = body.name.trim();
        if (body?.color) col.color = body.color;
        return ok({ ok: true });
      }
      if (method === 'DELETE') {
        if (columnasDelEmbudo.length <= 1) return ok({ ok: false, reason: 'last_stage', error: 'Un embudo necesita al menos una columna.' });
        // Como el servidor de verdad: las tarjetas NO se borran, se quedan sin
        // columna y vuelven por su estado.
        const sueltas = tarjetasDelEmbudo.filter((l) => l.stageId === dos);
        sueltas.forEach((l) => {
          const porEstado = columnasDelEmbudo.find((x) => x.id !== dos && x.mappedStatus === l.status);
          l.stageId = (porEstado || columnasDelEmbudo.find((x) => x.id !== dos)).id;
        });
        removeById(columnasDelEmbudo, dos);
        return ok({ ok: true, data: { leadsRecolocados: sueltas.length } });
      }
    }
    if (uno && dos === 'stages' && method === 'POST') {
      const col = { id: newId('et'), name: String(body?.name || 'Nueva'), position: columnasDelEmbudo.length, color: null, mappedStatus: String(body?.mappedStatus || 'NEW') };
      columnasDelEmbudo.push(col);
      return ok({ ok: true, data: col });
    }
    return notFound();
  }

  // Citas medicas: las dos pantallas piden lo mismo con distinto filtro.
  if ((rest = match('/api/v1/medical-appointments')) !== null) {
    const [, id, accion] = rest.split('/');
    const qs = new URLSearchParams(String(path).split('?')[1] || '');
    const conNombre = (c) => ({ ...c, clientName: NOMBRES_DE_LA_DEMO[c.clientId] || '' });

    if (!id && method === 'GET') {
      let out = citasMedicas.slice();
      const cliente = qs.get('clientId');
      if (cliente) out = out.filter((c) => c.clientId === cliente);
      const est = (qs.get('status') || '').split(',').filter(Boolean);
      if (est.length) out = out.filter((c) => est.includes(c.status));
      const desde = qs.get('from');
      const hasta = qs.get('to');
      if (desde) out = out.filter((c) => new Date(c.startsAt) >= new Date(desde));
      if (hasta) out = out.filter((c) => new Date(c.startsAt) <= new Date(hasta));
      const texto = (qs.get('q') || '').trim().toLowerCase();
      if (texto) out = out.filter((c) => (NOMBRES_DE_LA_DEMO[c.clientId] || '').toLowerCase().includes(texto));
      out.sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
      return ok({ ok: true, data: out.map(conNombre), hayMas: false });
    }
    if (!id && method === 'POST') {
      const row = {
        ...body, id: newId('cita'),
        // Como el servidor de verdad: el proveedor se arma aqui, no lo manda
        // la pantalla.
        proveedor: proveedorDeLaDemo(body),
        direccion: [body?.dir1, body?.dir2, [body?.ciudad, [body?.estado, body?.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', '),
        confirmadoPor: body?.status === 'confirmada' ? 'Lissette' : '',
        confirmedAt: body?.status === 'confirmada' ? at(0) : null,
        enElCalendario: !!body?.enElCalendario,
        creadoPor: 'Lissette', actualizadoPor: 'Lissette', createdAt: at(0), updatedAt: at(0),
      };
      citasMedicas.push(row);
      return ok({ ok: true, data: conNombre(row), recortados: [] });
    }
    const i = citasMedicas.findIndex((c) => c.id === id);
    if (id && accion === 'status' && method === 'PATCH') {
      if (i === -1) return notFound();
      citasMedicas[i] = {
        ...citasMedicas[i], status: body?.status, updatedAt: at(0),
        ...(body?.status === 'confirmada' && !citasMedicas[i].confirmadoPor
          ? { confirmadoPor: 'Lissette', confirmedAt: at(0) } : {}),
      };
      return ok({ ok: true, data: conNombre(citasMedicas[i]) });
    }
    if (id && method === 'PUT') {
      if (i === -1) return notFound();
      citasMedicas[i] = {
        ...citasMedicas[i], ...body, id,
        proveedor: proveedorDeLaDemo(body, citasMedicas[i]),
        direccion: [body?.dir1, body?.dir2, [body?.ciudad, [body?.estado, body?.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', '),
        enElCalendario: !!body?.enElCalendario,
        actualizadoPor: 'Lissette', updatedAt: at(0),
      };
      return ok({ ok: true, data: conNombre(citasMedicas[i]), recortados: [] });
    }
    if (id && method === 'DELETE') {
      if (i === -1) return notFound();
      citasMedicas.splice(i, 1);
      return ok({ ok: true });
    }
  }

  return null;
}
